import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Copy, Loader2, Mail, Phone, MapPin, Calendar, Home,
  FileText, Trash2, Check, CheckCircle2, Save, MailCheck, BadgeDollarSign, FilePlus, Archive,
  Link2, ExternalLink, Target, RefreshCcw, AlertTriangle, Lock, CalendarPlus,
  Languages, MessageCircle, Star, Repeat, Eye, ChevronDown, Tag, Clock,
} from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { StatusBadge } from "../components/app/StatusBadge";
import { LeadScoreBadge } from "../components/app/LeadScoreBadge";
import { RebookPreviewDialog } from "../components/app/RebookPreviewDialog";
import { ReviewRequestPreviewDialog } from "../components/app/ReviewRequestPreviewDialog";
import { isMissedLead, formatMissedDuration } from "../lib/leadAlerts";
import { guessLanguageFromText } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "quoted", "booked", "closed", "cancelled"];
// Cancel-reason enum mirrors the backend `CancelReason` Literal. Order
// matters here — the dropdown renders in this sequence.
const CANCEL_REASONS = [
  "too_expensive",
  "no_response",
  "went_with_competitor",
  "rescheduled",
  "changed_mind",
  "other",
];
const LANGUAGE_LABELS = { en: "English", es: "Español" };

// Visibility chip — a tiny pill that signals whether a section is shown
// to the customer on the public quote (green) or stays internal to the
// owner (yellow). Pure presentational; renders inline next to a heading.
// Kept ultra-compact so it never competes with the section title.
const VisibilityChip = ({ kind, t }) => {
  const customer = kind === "customer";
  const cls = customer
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : "bg-amber-50 text-amber-800 border-amber-200";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${cls}`}
      data-testid={`vis-chip-${kind}`}
    >
      <span aria-hidden="true">{customer ? "🟢" : "🟡"}</span>
      {t(customer ? "lead.vis.customer" : "lead.vis.owner")}
    </span>
  );
};

// Renders the structured AI quote (price summary + included + add-ons +
// customer message + owner-only "why this price"). Falls back gracefully
// when individual fields are missing. Customer-facing copy is clearly
// labelled as estimate, never claims accounting accuracy.
//
// Editable fields (Suggested Price + Customer-Facing Message) are wired
// through optional callbacks so the owner can override the AI draft
// before sharing. Both fields are debounced/save-on-blur via the parent
// — this component just surfaces the UI and reports back.
const StructuredQuoteView = ({
  quote,
  t,
  onPriceSave,
  onMessageSave,
  priceSaving,
  messageSaving,
}) => {
  // Local editable state — seeded from the quote prop, refreshed when the
  // underlying quote changes (e.g. after a Generate Quote regeneration).
  const [priceInput, setPriceInput] = React.useState(
    typeof quote?.suggested_price === "number" ? String(Math.round(quote.suggested_price)) : ""
  );
  const [messageInput, setMessageInput] = React.useState(
    typeof quote?.customer_message === "string" ? quote.customer_message : ""
  );
  React.useEffect(() => {
    setPriceInput(
      typeof quote?.suggested_price === "number" ? String(Math.round(quote.suggested_price)) : ""
    );
  }, [quote?.suggested_price]);
  React.useEffect(() => {
    setMessageInput(typeof quote?.customer_message === "string" ? quote.customer_message : "");
  }, [quote?.customer_message]);

  if (!quote || typeof quote !== "object") return null;
  const low = quote.price_range_low;
  const high = quote.price_range_high;
  const conf = quote.confidence;
  const included = Array.isArray(quote.included) ? quote.included : [];
  const addOns = Array.isArray(quote.add_ons) ? quote.add_ons : [];
  const assumptions = Array.isArray(quote.assumptions) ? quote.assumptions : [];
  const why = quote.why_this_price;
  const next = quote.next_best_action;

  const confTone =
    conf === "High" || conf === "Alta"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : conf === "Low" || conf === "Baja"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-sky-50 text-sky-800 border-sky-200";

  const fmt = (v) =>
    typeof v === "number" && !Number.isNaN(v)
      ? `$${Math.round(v).toLocaleString()}`
      : "—";

  return (
    <div className="mt-4 space-y-3" data-testid="structured-quote-view">
      {/* Visibility summary — compact two-line legend at the top so the
          owner knows what the green vs yellow chips mean before scanning
          the rest of the structured quote card. */}
      <div
        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-[11px] text-gray-600 leading-relaxed"
        data-testid="visibility-summary"
      >
        <p className="font-semibold text-gray-700 mb-0.5">
          {t("lead.vis.summaryTitle")}
        </p>
        <p>
          <span aria-hidden="true">🟢</span> {t("lead.vis.summaryCustomer")}
        </p>
        <p>
          <span aria-hidden="true">🟡</span> {t("lead.vis.summaryOwner")}
        </p>
      </div>

      {/* Price summary card — Estimated Quote is customer-visible; the
          'Why this price' sub-card and Confidence pill stay internal. */}
      <div className="rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-700">
                {t("lead.quoteSuggestedPrice")}
              </p>
              <VisibilityChip kind="customer" t={t} />
            </div>
            <p
              className="font-display text-3xl font-semibold text-gray-900 mt-0.5"
              data-testid="quote-suggested-price"
            >
              {fmt(quote.suggested_price)}
            </p>
            <p className="text-[11px] text-gray-500 mt-1 leading-snug" data-testid="quote-customer-only-helper">
              {t("lead.quoteCustomerOnlyHelper")}
            </p>
            {/* Editable Suggested Price input — owner can override the
                AI's recommendation before sharing. Save-on-blur (or Enter)
                to the parent's `onPriceSave`. The big display above
                stays the source of truth for downstream consumers
                (customer view + WhatsApp text + estimated_job_value). */}
            {onPriceSave ? (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="quote-price-edit">
                <label
                  htmlFor="quote-price-input"
                  className="text-[11px] font-medium text-gray-600"
                >
                  {t("lead.quotePriceEditLabel")}
                </label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-600">$</span>
                  <input
                    id="quote-price-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="1"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    onBlur={() => {
                      const raw = priceInput.trim();
                      if (!raw) return;
                      const n = Number(raw);
                      if (Number.isFinite(n) && n >= 0) {
                        const current = typeof quote.suggested_price === "number"
                          ? Math.round(quote.suggested_price)
                          : null;
                        if (Math.round(n) !== current) onPriceSave(n);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="w-24 h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-700"
                    data-testid="quote-price-input"
                    disabled={!!priceSaving}
                  />
                </div>
                {priceSaving ? (
                  <span className="text-[11px] text-gray-500" data-testid="quote-price-saving">
                    {t("lead.quoteSaving")}
                  </span>
                ) : (
                  <span className="text-[11px] text-gray-500">
                    {t("lead.quotePriceEditHelper")}
                  </span>
                )}
              </div>
            ) : null}
          </div>
          {conf ? (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${confTone}`}
              data-testid="quote-confidence-pill"
              title={t("lead.vis.ownerHelper")}
            >
              {t("lead.quoteConfidence")}: {conf}
            </span>
          ) : null}
        </div>

        {/* Range — explicitly owner-only, visually separated from the
            customer-visible Suggested Price above. Customers never see
            this. */}
        {(typeof low === "number" || typeof high === "number") ? (
          <div
            className="mt-3 rounded-lg bg-amber-50/40 border border-amber-200 p-3"
            data-testid="quote-range-owner-only"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                {t("lead.quoteRangeLabel")}
              </p>
              <VisibilityChip kind="owner" t={t} />
            </div>
            <p className="text-sm font-medium text-gray-900 mt-1">
              {fmt(low)} – {fmt(high)} <span className="text-xs font-normal text-gray-500">· {t("lead.quoteEstimated")}</span>
            </p>
            <p className="text-[11px] text-amber-700/70 mt-1 italic">
              {t("lead.vis.ownerHelper")}
            </p>
          </div>
        ) : null}
        {why ? (
          <div
            className="mt-3 rounded-lg bg-white/70 border border-sky-100 p-3"
            data-testid="quote-why-this-price"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                {t("lead.quoteWhyThisPrice")}
              </p>
              <VisibilityChip kind="owner" t={t} />
            </div>
            <p className="text-[13px] text-gray-700 mt-1 leading-relaxed">{why}</p>
            <p className="text-[11px] text-gray-400 mt-1 italic">
              {t("lead.vis.ownerHelper")}
            </p>
          </div>
        ) : null}
      </div>

      {/* Service Highlights (customer-visible) + Upsell Opportunities
          (owner-only) side by side on wide. */}
      <div className="grid gap-3 md:grid-cols-2">
        {included.length > 0 ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="quote-included-card">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
                {t("lead.quoteIncluded")}
              </p>
              <VisibilityChip kind="customer" t={t} />
            </div>
            <ul className="mt-2 space-y-1">
              {included.map((i, idx) => (
                <li key={idx} className="text-[13px] text-gray-700 flex gap-2">
                  <span className="text-emerald-600 mt-0.5">✓</span>
                  <span>{String(i)}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-emerald-700/70 mt-2 italic">
              {t("lead.vis.customerHelper")}
            </p>
          </div>
        ) : null}
        {addOns.length > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="quote-addons-card">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                {t("lead.upsellTitle")}
              </p>
              <VisibilityChip kind="owner" t={t} />
            </div>
            <ul className="mt-2 space-y-1">
              {addOns.map((a, idx) => (
                <li
                  key={idx}
                  className="text-[13px] text-gray-700 flex items-center justify-between gap-3"
                >
                  <span>{a?.name || "—"}</span>
                  {typeof a?.price === "number" ? (
                    <span className="font-semibold text-gray-900">{fmt(a.price)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-amber-800/70 mt-2 italic">
              {t("lead.upsellHelper")}
            </p>
          </div>
        ) : null}
      </div>

      {/* Customer-facing message — editable. The owner can edit, add,
          remove, or personalize the AI draft before sharing. Save-on-blur
          to the parent's `onMessageSave`. The edited value is what the
          public quote page renders verbatim. */}
      {(typeof quote.customer_message === "string" || onMessageSave) ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="quote-customer-message-card">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {t("lead.quoteCustomerMessage")}
            </p>
            <VisibilityChip kind="customer" t={t} />
            {messageSaving ? (
              <span className="text-[11px] text-gray-500" data-testid="quote-message-saving">
                {t("lead.quoteSaving")}
              </span>
            ) : null}
          </div>
          {onMessageSave ? (
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onBlur={() => {
                const next = messageInput;
                const current = typeof quote.customer_message === "string" ? quote.customer_message : "";
                if (next !== current) onMessageSave(next);
              }}
              rows={5}
              className="mt-2 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-800 leading-relaxed focus:outline-none focus:ring-2 focus:ring-green-700 whitespace-pre-line"
              data-testid="quote-customer-message-input"
              placeholder={t("lead.quoteCustomerMessagePlaceholder")}
              disabled={!!messageSaving}
            />
          ) : (
            <p className="mt-2 text-[13px] text-gray-800 leading-relaxed whitespace-pre-line">
              {quote.customer_message}
            </p>
          )}
          <p className="mt-2 text-[11px] text-gray-400 italic">{t("lead.quoteCustomerHelper")}</p>
        </div>
      ) : null}

      {/* Owner-only footer: assumptions + next best action */}
      {(assumptions.length > 0 || next) ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 p-4" data-testid="quote-owner-footer">
          {assumptions.length > 0 ? (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {t("lead.quoteAssumptions")}
                </p>
                <VisibilityChip kind="owner" t={t} />
              </div>
              <ul className="mt-1 list-disc list-inside text-[12px] text-gray-600 space-y-0.5">
                {assumptions.map((a, idx) => (
                  <li key={idx}>{String(a)}</li>
                ))}
              </ul>
            </>
          ) : null}
          {next ? (
            <div className={assumptions.length > 0 ? "mt-3" : ""}>
              {assumptions.length === 0 ? (
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    {t("lead.quoteNextBestAction")}
                  </p>
                  <VisibilityChip kind="owner" t={t} />
                </div>
              ) : null}
              <p
                className="text-[12px] text-emerald-800"
                data-testid="quote-next-best-action"
              >
                {assumptions.length > 0 ? (
                  <span className="font-semibold">{t("lead.quoteNextBestAction")}:</span>
                ) : null}{" "}
                {next}
              </p>
            </div>
          ) : null}
          <p className="text-[11px] text-gray-400 mt-2 italic">
            {t("lead.vis.ownerHelper")}
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const locked = Boolean(user) && !user.has_full_access;
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  // Editable fields — keep local state separate from lead so user can edit freely.
  const [notes, setNotes] = useState("");
  const [replyDraft, setReplyDraft] = useState("");
  const [quoteDraft, setQuoteDraft] = useState("");

  // Per-lead language selection for AI generation. Defaults are seeded from
  // the customer's message via `guessLanguageFromText` once the lead loads,
  // so a Spanish-language inquiry auto-sets both selectors to Spanish — but
  // the owner can override either side independently with one click.
  const [replyLang, setReplyLang] = useState("en");
  const [quoteLang, setQuoteLang] = useState("en");

  // AI v2: tone selection + customer-reply context. `replyTone` defaults
  // to "professional" per spec. `customerContext` is the optional latest
  // customer message (e.g. "That's too expensive", "Can you do Friday?")
  // — when non-empty we pass it to /generate-reply so the AI responds to
  // that specific message instead of doing a first-touch reply.
  // `replyOptions` holds the 3 options returned by the latest call.
  const [replyTone, setReplyTone] = useState("professional");
  const [customerContext, setCustomerContext] = useState("");
  const [replyOptions, setReplyOptions] = useState([]);
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);

  // On-demand translation of the customer's incoming message — pure display,
  // never persisted. `translatedMessage` holds the most recent translation
  // and `showTranslated` toggles between the original and the translated
  // text so the owner can flip back.
  const [translatedMessage, setTranslatedMessage] = useState("");
  const [translatedTarget, setTranslatedTarget] = useState(null);
  const [showTranslated, setShowTranslated] = useState(false);
  const [translatingMessage, setTranslatingMessage] = useState(false);

  const [generatingReply, setGeneratingReply] = useState(false);
  // Tracks the exact `customerContext` value the owner last clicked
  // Regenerate on. After a successful reply generation, the question/
  // objection card auto-yields to "Copy Reply" so the engine doesn't get
  // stuck on "Reply first" once we've actually drafted the answer.
  // Re-edits to customerContext (or a new pasted message) re-trigger the
  // intent classifier.
  const [lastAnsweredContext, setLastAnsweredContext] = useState("");
  // Lead Details — EXPANDED by default on all viewports (mobile + desktop)
  // so the owner immediately sees the customer's submitted request when
  // they arrive from the new-lead SMS or the dashboard. Users can still
  // collapse it on mobile via the header toggle (chevron); desktop keeps
  // it always open via `md:block`. Historically this defaulted to `false`
  // on mobile which buried the customer's info behind the AI workflow.
  const [leadDetailsOpen, setLeadDetailsOpen] = useState(true);
  const [generatingQuote, setGeneratingQuote] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingReply, setSavingReply] = useState(false);
  const [savingQuote, setSavingQuote] = useState(false);
  const [markingContacted, setMarkingContacted] = useState(false);
  const [markingQuoted, setMarkingQuoted] = useState(false);
  const [copied, setCopied] = useState({ reply: false, quote: false, both: false, link: false, linkEs: false, message: false });
  const [creatingQuoteLink, setCreatingQuoteLink] = useState(false);
  const [quoteLink, setQuoteLink] = useState(null);
  // Owner-edit saves for Suggested Price + Customer-Facing Message
  // (Step 2). Both hit `PATCH /api/leads/{id}/quote-edit` which mutates
  // `ai_quote_structured.suggested_price` / `.customer_message` AND
  // re-renders the legacy `ai_quote` text so all downstream consumers
  // (WhatsApp message + quote-link snapshot + estimated_job_value) read
  // the edited value.
  const [quotePriceSaving, setQuotePriceSaving] = useState(false);
  const [quoteMessageSaving, setQuoteMessageSaving] = useState(false);
  const [analyzingScore, setAnalyzingScore] = useState(false);
  const [requestingReview, setRequestingReview] = useState(false);
  const [sendingRebook, setSendingRebook] = useState(false);
  const [rebookPreviewOpen, setRebookPreviewOpen] = useState(false);
  const [reviewPreviewOpen, setReviewPreviewOpen] = useState(false);

  // Refs used by the inline workflow guidance cards to scroll the next
  // step into view when the owner clicks the suggested CTA. Refs sit on
  // the section containers so the section header lands near the top of
  // the viewport (better than `id` anchors — no URL mutation).
  const aiReplyCardRef = useRef(null);
  const aiQuoteCardRef = useRef(null);
  const quoteLinkCardRef = useRef(null);
  const createQuoteBtnRef = useRef(null);

  // Smooth-scroll helper for the workflow guidance buttons. Positions the
  // target card's TOP edge fully below the sticky Tasklio header with a
  // small breathing gap so the card title + top border are always visible
  // (never partially hidden underneath the header). We measure the header
  // height at click-time via `getBoundingClientRect()` so responsive
  // changes (e.g. compact mobile header, future banner) are picked up
  // dynamically — no hardcoded pixel values that can rot.
  //
  // Fallback: when `window.scrollTo` isn't available (very old runtimes)
  // we fall back to `scrollIntoView` so we at least get the user close.
  const SCROLL_HEADER_GAP_PX = 14; // 12–16px visual breathing room
  const scrollToRef = (ref) => {
    const node = ref?.current;
    if (!node) return;
    try {
      // Read the actual on-screen height of the sticky header. Tag added
      // in Navbar (`data-sticky-header="true"`) so this survives future
      // markup changes as long as the attribute is preserved. When the
      // header isn't found (offline layouts, tests), assume 0.
      const headerEl = document.querySelector('[data-sticky-header="true"]');
      const headerH = headerEl ? Math.round(headerEl.getBoundingClientRect().height) : 0;
      // Absolute page-space Y of the target element's top edge.
      const targetTop = node.getBoundingClientRect().top + window.scrollY;
      const desiredTop = Math.max(0, targetTop - headerH - SCROLL_HEADER_GAP_PX);
      if (typeof window.scrollTo === "function") {
        window.scrollTo({ top: desiredTop, behavior: "smooth" });
      } else {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } catch {
      // Any unexpected error → fall back to the browser default so the
      // user still ends up somewhere reasonable.
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Recommended-Next-Action → Step 1 handoff. When the owner taps
  // "Generate Reply" (or "Regenerate Reply") on the top card, we
  // (a) immediately scroll to the Step 1 reply section so the loading
  // spinner is visible on mobile, and (b) trigger the same underlying
  // generation used by the in-section button — no duplicated logic. The
  // scroll fires first so the "Drafting reply…" state renders where the
  // user is looking. Wrapped in requestAnimationFrame so the scroll
  // starts on the very next paint even when the button was itself part
  // of a scrollable region (avoids a Safari mobile quirk).
  const scrollAndGenReply = () => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => scrollToRef(aiReplyCardRef));
    } else {
      scrollToRef(aiReplyCardRef);
    }
    // Delegate to the existing genReply — never duplicate the API call.
    genReply();
  };

  // Symmetric handoff for future quote-generation from the top card
  // (requirement #7): scroll to Step 2 first, then start generation
  // there so the user visually follows the action.
  const scrollAndGenQuote = () => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => scrollToRef(aiQuoteCardRef));
    } else {
      scrollToRef(aiQuoteCardRef);
    }
    genQuote();
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      setNotes(data.internal_notes || "");
      setReplyDraft(data.ai_reply || "");
      setQuoteDraft(data.ai_quote || "");
      // Restore previously generated reply options (if any) so the owner
      // can re-pick after navigating away and coming back. selected index
      // resets to 0 — Phase-1 just remembers the set, not which one was
      // picked.
      const opts = Array.isArray(data.ai_reply_options) ? data.ai_reply_options : [];
      setReplyOptions(opts);
      setSelectedOptionIdx(0);
      setCustomerContext("");
      // Auto-detect language from the customer's message and seed both
      // generators with that default. Owner can change with one click.
      const detected = guessLanguageFromText(data.message || "");
      setReplyLang(detected);
      setQuoteLang(detected);
      // Reset translation state for this lead — never carry over from a
      // previous lead detail view.
      setTranslatedMessage("");
      setTranslatedTarget(null);
      setShowTranslated(false);
    } catch (err) {
      toast.error("Could not load lead");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const updateStatus = async (newStatus) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status: newStatus });
      setLead(data);
      toast.success(`Status set to ${newStatus}`);
    } catch {
      toast.error("Couldn't update status");
    }
  };

  // Cancel-reason dropdown — only PATCHes when the value actually changes,
  // and never blocks the status select (the reason is optional).
  const updateCancelReason = async (reason) => {
    if (!reason || reason === (lead?.cancel_reason || "")) return;
    try {
      const { data } = await api.patch(`/leads/${id}`, { cancel_reason: reason });
      setLead(data);
    } catch {
      toast.error("Couldn't save reason");
    }
  };

  // Archive replaces destructive delete for non-admin users. Optimistic UI
  // would be nice but a single network call + redirect keeps it simple.
  const archiveLead = async () => {
    try {
      await api.post(`/leads/${id}/archive`);
      toast.success(t("lead.archiveSuccess"));
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("lead.archiveError"));
    }
  };

  // Admins-only — permanent delete is gated on the backend (403 for
  // non-admins), but we ALSO hide the button below to keep the UI honest.
  const permanentDeleteLead = async () => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't delete lead");
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, { internal_notes: notes });
      setLead(data);
      toast.success("Notes saved");
    } catch {
      toast.error("Couldn't save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  // ---- Estimated job value (Business Insights, customer-facing) ----
  // Local edit-on-blur model: typing updates the input string; we only PATCH
  // when the field loses focus AND the value parsed back differs from the
  // saved lead doc. Empty input = no value (we never write 0 implicitly).
  const [jobValueInput, setJobValueInput] = useState("");
  const [savingJobValue, setSavingJobValue] = useState(false);
  useEffect(() => {
    if (lead) {
      setJobValueInput(
        lead.estimated_job_value != null ? String(lead.estimated_job_value) : "",
      );
    }
  }, [lead?.id, lead?.estimated_job_value]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveJobValue = async () => {
    if (!lead) return;
    const raw = jobValueInput.trim();
    const parsed = raw === "" ? null : Number(raw);
    if (raw !== "" && (Number.isNaN(parsed) || parsed < 0)) {
      toast.error("Enter a positive number, or leave blank.");
      setJobValueInput(
        lead.estimated_job_value != null ? String(lead.estimated_job_value) : "",
      );
      return;
    }
    if ((lead.estimated_job_value ?? null) === (parsed ?? null)) return;
    setSavingJobValue(true);
    try {
      // PATCH endpoint ignores explicit `null` (legacy contract), so we send
      // 0 to "clear" the contribution. The backend treats 0 as "exclude from
      // revenue" (we only sum `> 0`). For non-zero updates, send the number.
      const { data } = await api.patch(`/leads/${id}`, {
        estimated_job_value: parsed ?? 0,
      });
      setLead(data);
      toast.success("Estimated value saved");
    } catch {
      toast.error("Couldn't save value");
    } finally {
      setSavingJobValue(false);
    }
  };

  const saveReply = async () => {
    setSavingReply(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, { ai_reply: replyDraft });
      setLead(data);
      toast.success("Reply saved");
    } catch {
      toast.error("Couldn't save reply");
    } finally {
      setSavingReply(false);
    }
  };

  const saveQuote = async () => {
    setSavingQuote(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, { ai_quote: quoteDraft });
      setLead(data);
      toast.success("Quote saved");
    } catch {
      toast.error("Couldn't save quote");
    } finally {
      setSavingQuote(false);
    }
  };

  const genReply = async () => {
    setGeneratingReply(true);
    try {
      const { data } = await api.post(`/leads/${id}/generate-reply`, {
        language: replyLang,
        tone: replyTone,
        customer_context: customerContext.trim() || null,
      });
      const opts = Array.isArray(data.options) ? data.options : [];
      setLead((l) => ({ ...l, ai_reply: data.ai_reply, ai_reply_options: opts }));
      setReplyOptions(opts);
      setSelectedOptionIdx(0);
      setReplyDraft(data.ai_reply);
      // Mark this customerContext value as "answered" so the Recommended
      // Next Action card flips from "Reply first" to "Copy Reply"
      // immediately, until the owner types/pastes a new follow-up.
      setLastAnsweredContext(customerContext.trim());
      toast.success("3 reply options generated — pick one or edit");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGeneratingReply(false);
    }
  };

  // Clicking a different option card swaps the active reply text. We also
  // persist the swap to the server so refresh keeps the right pick (this
  // is just a PATCH on ai_reply — ai_reply_options stays intact for re-pick).
  const pickReplyOption = async (idx) => {
    const opt = replyOptions[idx];
    if (!opt) return;
    setSelectedOptionIdx(idx);
    setReplyDraft(opt.body);
    try {
      await api.patch(`/leads/${id}`, { ai_reply: opt.body });
      setLead((l) => ({ ...l, ai_reply: opt.body }));
    } catch {
      // Non-fatal: the textarea is already updated locally; owner can hit
      // "Save edits" if they care.
    }
  };

  const genQuote = async () => {
    setGeneratingQuote(true);
    try {
      const { data } = await api.post(`/leads/${id}/generate-quote`, { language: quoteLang });
      setLead((l) => ({
        ...l,
        ai_quote: data.ai_quote,
        ai_quote_structured: data.ai_quote_structured || null,
      }));
      setQuoteDraft(data.ai_quote);
      toast.success("Quote generated — tweak as needed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGeneratingQuote(false);
    }
  };

  // Toggle between the customer's original message and an on-demand
  // translation. We translate ONCE per target language and cache the
  // result locally so the user can flip back and forth instantly.
  const translateCustomerMessage = async () => {
    if (!lead?.message?.trim()) return;
    // If we're currently showing a translation, flip back to the original.
    if (showTranslated) {
      setShowTranslated(false);
      return;
    }
    // Original language guess — drives which target we want.
    const detected = guessLanguageFromText(lead.message || "");
    const target = detected === "es" ? "en" : "es";
    // If we already have a cached translation for this target, just flip.
    if (translatedMessage && translatedTarget === target) {
      setShowTranslated(true);
      return;
    }
    setTranslatingMessage(true);
    try {
      const { data } = await api.post(`/leads/${id}/translate-message`, { target });
      setTranslatedMessage(data.translated || "");
      setTranslatedTarget(target);
      setShowTranslated(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Translation failed");
    } finally {
      setTranslatingMessage(false);
    }
  };

  const copyText = async (text, key, label) => {
    if (!text?.trim()) {
      toast.error(`Nothing to copy`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied((c) => ({ ...c, [key]: true }));
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 2000);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const openCalendar = () => {
    // Build the event fields once — used by both the Google Calendar URL path
    // (desktop) and the .ics download fallback (mobile, or if URL build fails).
    const now = new Date();
    // Priority: explicit preferred_date (from the lead) → created_at → today.
    // Google Calendar wants YYYYMMDD all-day, so we parse strictly.
    const parseLeadDate = () => {
      const raw = lead?.preferred_date || lead?.created_at || null;
      if (!raw) return now;
      const d = new Date(raw);
      return isNaN(d.getTime()) ? now : d;
    };
    const eventDate = parseLeadDate();
    const toYMD = (d) => d.toISOString().slice(0, 10).replace(/-/g, "");
    const startDate = toYMD(eventDate);
    const endDateObj = new Date(eventDate);
    endDateObj.setDate(endDateObj.getDate() + 1);
    const endDate = toYMD(endDateObj);
    const title = `Cleaning lead — ${lead?.name || "Customer"}`;
    const descriptionLines = [
      `Service: ${lead?.service_type || "-"}`,
      `Phone: ${lead?.phone || "-"}`,
      `Email: ${lead?.email || "-"}`,
      `Message: ${lead?.message || "-"}`,
    ];

    // Detect mobile once — on iOS/Android we prefer the .ics path because it
    // hands off to the native Calendar app, which is how most users on those
    // devices expect "Add to calendar" to feel. On desktop we open the Google
    // Calendar event-creation page in a new tab with fields prefilled.
    const ua = navigator.userAgent || "";
    const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);

    const downloadIcs = () => {
      const esc = (v) =>
        String(v ?? "-")
          .replace(/\\/g, "\\\\")
          .replace(/\n/g, "\\n")
          .replace(/,/g, "\\,")
          .replace(/;/g, "\\;");
      const description = descriptionLines.map(esc).join("\\n");
      const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Tasklio AI//Lead Calendar//EN
BEGIN:VEVENT
UID:${lead?.id || Date.now()}@tasklio
DTSTAMP:${startDate}T000000Z
DTSTART;VALUE=DATE:${startDate}
DTEND;VALUE=DATE:${endDate}
SUMMARY:${esc(title)}
DESCRIPTION:${description}
END:VEVENT
END:VCALENDAR`;
      const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tasklio-lead.ics";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    // --- Desktop: Google Calendar event-creation URL ---
    if (!isMobile) {
      try {
        const params = new URLSearchParams({
          action: "TEMPLATE",
          text: title,
          // Google Calendar all-day format: YYYYMMDD/YYYYMMDD (end is exclusive).
          dates: `${startDate}/${endDate}`,
          details: descriptionLines.join("\n"),
        });
        const gcalUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
        const popup = window.open(gcalUrl, "_blank", "noopener,noreferrer");
        if (popup) return;
        // Popup blocked → fall through to .ics download as a guaranteed handoff.
      } catch {
        // Build or open failed → fall back.
      }
    }

    // --- Mobile (iOS/Android) or desktop fallback: .ics download ---
    try {
      downloadIcs();
    } catch {
      // Last-ditch fallback so the button is never a dead-end.
      window.open("https://calendar.google.com/calendar/u/0/r/eventedit", "_blank", "noopener,noreferrer");
    }
  };

  const copyBoth = () => {
    const combined = [
      replyDraft?.trim() && replyDraft.trim(),
      quoteDraft?.trim() && `\n\n— Price estimate —\n${quoteDraft.trim()}`,
    ]
      .filter(Boolean)
      .join("");
    copyText(combined, "both", "Reply + quote");
  };

  const quickStatus = async (newStatus, setter) => {
    setter(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, { status: newStatus });
      setLead(data);
      toast.success(`Marked as ${newStatus}`);
    } catch {
      toast.error(`Couldn't update status`);
    } finally {
      setter(false);
    }
  };

  const createQuoteLink = async () => {
    setCreatingQuoteLink(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post(`/quotes/create`, {
        lead_id: id,
        origin_url: origin,
      });
      const url = data.public_url || `${origin}/q/${data.quote_id}`;
      setQuoteLink(url);
      toast.success("Quote link ready — copy & send to your customer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create quote link");
    } finally {
      setCreatingQuoteLink(false);
    }
  };

  // Owner edits the AI-suggested customer-facing price. PATCHes the lead
  // and merges the returned doc back so `ai_quote_structured.suggested_price`
  // + legacy `ai_quote` text + `estimated_job_value` all reflect the new
  // value immediately. Step 3's "Create quote link" snapshots these into
  // the public quote at the moment the link is created.
  const savePriceEdit = async (newPrice) => {
    setQuotePriceSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}/quote-edit`, {
        suggested_price: newPrice,
      });
      setLead(data);
      // Keep the textarea in sync — the legacy ai_quote text just changed.
      if (typeof data?.ai_quote === "string") setQuoteDraft(data.ai_quote);
      toast.success(t("lead.quotePriceUpdated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("lead.quotePriceUpdateError"));
    } finally {
      setQuotePriceSaving(false);
    }
  };

  // Owner edits the AI customer-facing message. Same pipeline as the
  // price edit; the public quote page renders this string verbatim.
  const saveCustomerMessageEdit = async (newMessage) => {
    setQuoteMessageSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}/quote-edit`, {
        customer_message: newMessage,
      });
      setLead(data);
      toast.success(t("lead.quoteMessageUpdated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("lead.quoteMessageUpdateError"));
    } finally {
      setQuoteMessageSaving(false);
    }
  };

  // Build a customer-ready quote message (greeting + link + sign-off) in
  // either EN or ES. Falls back to "Hi,"/"Hola," when the lead has no name
  // and to a generic business name when the owner hasn't set one. The ES
  // variant appends `?lang=es` to the link so the public quote page opens
  // in Spanish (same convention used by sendViaWhatsApp).
  const buildQuoteMessage = (useEs) => {
    const customerName = (lead?.name || "").trim();
    const businessName = (user?.business_name || "").trim();
    if (useEs) {
      const greeting = customerName ? `Hola ${customerName},` : "Hola,";
      const sig = businessName || "tu negocio de limpieza";
      const sep = quoteLink.includes("?") ? "&" : "?";
      const link = `${quoteLink}${sep}lang=es`;
      return [
        greeting,
        "He preparado tu cotización.",
        `Puedes revisarla aquí:\n${link}`,
        "Si tienes alguna pregunta, con gusto te ayudo.",
        `Gracias,\n${sig}`,
      ].join("\n\n");
    }
    const greeting = customerName ? `Hi ${customerName},` : "Hi,";
    const sig = businessName || "your cleaning business";
    return [
      greeting,
      "I've prepared your quote.",
      `You can review it here:\n${quoteLink}`,
      "Let me know if you have any questions.",
      `Thank you,\n${sig}`,
    ].join("\n\n");
  };

  // Build a mailto: URL opening the customer's email client with subject
  // and body pre-filled. The body matches Step 3's customer-facing copy:
  // greeting + quote link + thank-you. The owner can edit before sending.
  const buildQuoteMailto = () => {
    if (!quoteLink) return "#";
    const customerEmail = (lead?.email || "").trim();
    const useEs = shouldUseSpanish();
    const sep = quoteLink.includes("?") ? "&" : "?";
    const link = useEs ? `${quoteLink}${sep}lang=es` : quoteLink;
    const subject = useEs ? "Tu cotización de limpieza" : "Your Cleaning Quote";
    const body = useEs
      ? `Hola,\n\nHe preparado tu cotización para que la revises.\n\n${link}\n\nGracias.`
      : `Hi,\n\nI've prepared your quote for review.\n\n${link}\n\nThank you.`;
    const params = new URLSearchParams({
      subject,
      body,
    }).toString().replace(/\+/g, "%20");
    return `mailto:${encodeURIComponent(customerEmail)}?${params}`;
  };

  // Language signal for outbound messages — prefer an explicit quote/reply
  // language over the UI locale per spec ("Do NOT rely only on app UI
  // language if the lead/quote already has a language signal"). Defaults
  // to English when no signal is present.
  const shouldUseSpanish = () => quoteLang === "es" || replyLang === "es";

  // Primary copy action: short, friendly Quote Delivery Message — NOT the
  // Customer-Facing Message (that lives on the public quote page itself).
  // Intended for SMS / WhatsApp / Instagram / Facebook Messenger / any
  // text channel. Send via Email uses a separate email-shaped template.
  //
  // Format (per product spec):
  //   Hi [First Name],
  //   I've prepared your quote. 😊
  //   You can review it here:
  //   [QUOTE LINK]
  //   Let me know if you have any questions.
  //   Thank you!
  const buildCopyMessage = (useEs) => {
    if (!quoteLink) return "";
    const fullName = (lead?.name || "").trim();
    const firstName = fullName ? fullName.split(/\s+/)[0] : "";
    const sep = quoteLink.includes("?") ? "&" : "?";
    const link = useEs ? `${quoteLink}${sep}lang=es` : quoteLink;
    if (useEs) {
      const greeting = firstName ? `Hola ${firstName},` : "Hola,";
      return [
        greeting,
        "He preparado tu cotización. 😊",
        "Puedes revisarla aquí:",
        link,
        "Avísame si tienes alguna pregunta.",
        "¡Gracias!",
      ].join("\n\n");
    }
    const greeting = firstName ? `Hi ${firstName},` : "Hi,";
    return [
      greeting,
      "I've prepared your quote. 😊",
      "You can review it here:",
      link,
      "Let me know if you have any questions.",
      "Thank you!",
    ].join("\n\n");
  };

  // Primary copy action: full customer-ready message including the link.
  const copyQuoteMessage = async () => {
    if (!quoteLink) return;
    try {
      const msg = buildCopyMessage(shouldUseSpanish());
      await navigator.clipboard.writeText(msg);
      setCopied((c) => ({ ...c, message: true }));
      toast.success(t("lead.quoteMessageCopied"));
      setTimeout(() => setCopied((c) => ({ ...c, message: false })), 2000);
    } catch {
      toast.error(t("lead.copyError"));
    }
  };

  // Secondary copy action: bare URL only (kept for power users who want
  // to paste into an existing thread).
  const copyQuoteLink = async () => {
    if (!quoteLink) return;
    try {
      await navigator.clipboard.writeText(quoteLink);
      setCopied((c) => ({ ...c, link: true }));
      toast.success(t("lead.linkCopied"));
      setTimeout(() => setCopied((c) => ({ ...c, link: false })), 2000);
    } catch {
      toast.error(t("lead.copyError"));
    }
  };

  // "Share in Spanish" — appends ?lang=es to the quote link so the customer
  // lands on the public quote page with Spanish pre-selected. Non-destructive:
  // the stored `quoteLink` is unchanged; we only add the query param at copy
  // time. The public page reads `?lang=` and treats it as user-picked so the
  // auto-detect doesn't fight it.
  const copyQuoteLinkEs = async () => {
    if (!quoteLink) return;
    try {
      const sep = quoteLink.includes("?") ? "&" : "?";
      const esLink = `${quoteLink}${sep}lang=es`;
      await navigator.clipboard.writeText(esLink);
      setCopied((c) => ({ ...c, linkEs: true }));
      toast.success("Spanish quote link copied");
      setTimeout(() => setCopied((c) => ({ ...c, linkEs: false })), 2000);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  // Normalise a phone number into the digit-only international format
  // WhatsApp expects in wa.me/<digits>. Rules:
  //   - Strip every non-digit character.
  //   - If the cleaned number starts with '00', drop it (international prefix).
  //   - If the original had a leading '+', keep the digits as-is (already
  //     international).
  //   - Otherwise, if the cleaned number has 10 digits we assume US/Canada
  //     and prepend '1'. Shorter numbers are returned raw and we let
  //     WhatsApp's own validation show the error — we never guess a country
  //     code silently.
  // Returns null when there aren't enough digits to be a usable WA number.
  const waFormatPhone = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    const trimmed = raw.trim();
    const hadPlus = trimmed.startsWith("+");
    let digits = trimmed.replace(/\D+/g, "");
    if (!digits) return null;
    if (digits.startsWith("00")) digits = digits.slice(2);
    if (!hadPlus && digits.length === 10) digits = `1${digits}`;
    return digits.length >= 7 ? digits : null;
  };

  const waPhone = waFormatPhone(lead?.phone);

  // Extract the clean customer-facing price (e.g. "$280") from an AI quote.
  // The LLM-generated quote has internal analysis we MUST NEVER send to the
  // customer (Confidence, Reasoning, Next Best Action, Price Range, etc.).
  // Both EN and ES quotes keep the English "Suggested Price: $XXX" label by
  // design (so the earnings parser keeps working) — we look for that label
  // first, then fall back to the first dollar amount in the text.
  // Returns null if no price can be confidently extracted.
  const extractCustomerPrice = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    const labeled = raw.match(/suggested price\s*:\s*\$?\s*([\d,]+(?:\.\d{1,2})?)/i);
    if (labeled?.[1]) return `$${labeled[1].replace(/,/g, "")}`;
    const fallback = raw.match(/\$\s*([\d,]+(?:\.\d{1,2})?)/);
    if (fallback?.[1]) return `$${fallback[1].replace(/,/g, "")}`;
    return null;
  };

  const customerPrice = extractCustomerPrice(quoteDraft);

  // Send the combined customer-facing message via WhatsApp from the Quote
  // Link card. The message contains EXACTLY the three customer-facing parts
  // — AI reply + clean price + quote link — and NEVER any of the internal
  // quote analysis (Confidence, Reasoning, Next Best Action, Price Range).
  //
  //   EN: "{reply}\n\nEstimated quote: ${price}\n\nView and accept your quote here:\n{link}"
  //   ES: "{reply}\n\nCotización estimada: ${price}\n\nVea y acepte su cotización aquí:\n{link}?lang=es"
  //
  // Spanish trigger = either replyLang or quoteLang === 'es' (whichever the
  // owner just generated). encodeURIComponent on the full body, accents
  // round-trip safely.
  const sendViaWhatsApp = () => {
    if (!waPhone || !quoteLink) return;
    const reply = (replyDraft || "").trim();
    const useEs = shouldUseSpanish();

    // Rich path: when both an AI reply AND a customer-facing price are
    // ready, send the original reply + price + link message (best UX —
    // preserves the AI-personalised reply text).
    // Fallback path: otherwise, send the new customer-ready template so
    // WhatsApp never reduces to a bare link.
    let body;
    if (reply && customerPrice) {
      const sep = quoteLink.includes("?") ? "&" : "?";
      const link = useEs ? `${quoteLink}${sep}lang=es` : quoteLink;
      const priceLabel = useEs ? "Cotización estimada:" : "Estimated quote:";
      const linkLabel = useEs
        ? "Vea y acepte su cotización aquí:"
        : "View and accept your quote here:";
      body = [
        reply,
        `${priceLabel} ${customerPrice}`,
        `${linkLabel}\n${link}`,
      ].join("\n\n");
    } else {
      body = buildQuoteMessage(useEs);
    }

    const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(body)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp…");
  };

  const analyzeScore = async () => {
    setAnalyzingScore(true);
    try {
      const { data } = await api.post(`/leads/${id}/analyze-score`);
      setLead(data);
      toast.success("Lead scored");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't score this lead");
    } finally {
      setAnalyzingScore(false);
    }
  };

  const requestReview = async () => {
    setRequestingReview(true);
    try {
      const { data } = await api.post(`/leads/${id}/request-review`);
      setLead(data);
      toast.success("Review request sent");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send the review request");
    } finally {
      setRequestingReview(false);
    }
  };

  const sendRebookFollowup = async () => {
    setSendingRebook(true);
    try {
      const { data } = await api.post(`/leads/${id}/rebook-followup`);
      setLead(data);
      toast.success("Rebook reminder sent");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send the rebook reminder");
    } finally {
      setSendingRebook(false);
    }
  };

  // Legacy delete handler removed — replaced by archiveLead +
  // permanentDeleteLead (admin-only) defined above.

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9f9f8]">
        <Navbar />
        <div className="p-16 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-green-800" />
        </div>
      </div>
    );
  }
  if (!lead) return null;

  const replyDirty = replyDraft !== (lead.ai_reply || "");
  const quoteDirty = quoteDraft !== (lead.ai_quote || "");
  const notesDirty = notes !== (lead.internal_notes || "");

  // Optional micro-emphasis for the 3-step closer flow. Only the first
  // incomplete step is highlighted at a time so the page doesn't light up
  // multiple rings when a lead skipped a step (e.g. quote without reply).
  // Skipped once the lead is booked/closed so the page stops nudging.
  const flowActive = !["booked", "closed"].includes(lead.status);
  const step1Done = !!lead.ai_reply;
  const step2Done = !!lead.ai_quote;
  const step3Done = !!quoteLink;
  const step1Active = flowActive && !step1Done;
  const step2Active = flowActive && !step1Active && !step2Done;
  const step3Active = flowActive && !step1Active && !step2Active && !step3Done;
  // Subtle ring + soft green elevation for the active step. Single source of
  // truth so the three cards stay visually consistent.
  const stepRing =
    "ring-2 ring-green-500/30 ring-offset-1 shadow-[0_0_0_6px_rgba(74,222,128,0.06)] transition-shadow";

  // Numbered chip used on each action card. `state` controls visual:
  //   "done"   → green check
  //   "active" → green filled number with soft glow
  //   "idle"   → muted gray number (future step)
  const StepChip = ({ n, state }) => {
    const base =
      "inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold border transition-colors";
    const variant =
      state === "done"
        ? "bg-green-600 border-green-600 text-white"
        : state === "active"
          ? "bg-green-50 border-green-500 text-green-800 shadow-[0_0_0_3px_rgba(74,222,128,0.15)]"
          : "bg-gray-50 border-gray-200 text-gray-400";
    return (
      <span className={`${base} ${variant}`} data-testid={`step-chip-${n}`} aria-label={`Step ${n}${state === "done" ? " complete" : state === "active" ? " active" : ""}`}>
        {state === "done" ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : n}
      </span>
    );
  };
  const chipState = (active, done) => (done ? "done" : active ? "active" : "idle");

  // ─────────────────────────────────────────────────────────────────────
  // Recommended Next Action engine
  // ─────────────────────────────────────────────────────────────────────
  // Picks ONE primary next-step recommendation for the owner based on the
  // lead's current state. Ordered top-down — the first matching branch
  // wins, so we never show competing actions.
  //
  // Customer-interest signal: cleaning businesses paste follow-up
  // customer messages into either the original `lead.message` (when the
  // public form captured it) or `internal_notes` (when the owner
  // forwards an SMS/Instagram reply). We scan BOTH fields plus the
  // owner's live "Customer's latest reply" textarea (`customerContext`).
  //
  // Priority logic:
  //   1. Check NEGATIVE patterns first — if any match, return false.
  //   2. Then check POSITIVE patterns — any match returns true.
  // Negative wins by design so "sounds good but too expensive" stays in
  // reply/follow-up mode rather than pushing the owner to send a quote.
  const NEGATIVE_PATTERNS = [
    // English
    /\b(too expensive|too pricey|too high|too much|out of (my )?budget|can'?t afford|cannot afford)\b/i,
    /\b(not interested|no thanks|no thank you|no longer interested)\b/i,
    /\b(maybe later|some other time|another time|next time)\b/i,
    /\b(i('?| wi)?ll think about it|i need to think|let me think|thinking about it|will get back)\b/i,
    /\b(cancel|cancelled|cancelling|never mind|nevermind|forget it|disregard)\b/i,
    /\b(found (someone|another)|going (with )?someone else|chose another)\b/i,
    // Spanish
    /\b(demasiado caro|muy caro|fuera de presupuesto|no puedo pagar)\b/i,
    /\b(no me interesa|ya no me interesa|no gracias)\b/i,
    /\b(tal vez (despu[ée]s|luego|m[áa]s tarde)|quiz[áa]s? (despu[ée]s|luego|m[áa]s tarde)|otro d[íi]a)\b/i,
    /\b(lo voy a pensar|voy a pensarlo|lo pensar[ée]|d[ée]jame pensarlo)\b/i,
    /\b(cancelar|cancelado|olv[íi]dalo|d[ée]jalo)\b/i,
  ];

  // Question / objection patterns — when the customer's latest message
  // looks like a clarification request, price objection, or "what's
  // included?" question, the owner MUST reply first before sending a
  // quote. This category WINS over INTEREST_PATTERNS so messages like
  // "sounds good but why is it $220?" route to Reply, not Generate Quote.
  //
  // IMPORTANT: customers texting from SMS / Instagram / WhatsApp rarely
  // use proper punctuation. Detection MUST work without a trailing "?".
  // We rely on intent-bearing wording (modals + interrogatives + topic
  // keywords) rather than punctuation alone.
  const QUESTION_PATTERNS = [
    // ── English — explicit "why" / explainers ─────────────────────────
    /\bwhy\b/i,
    /\b(can|could) you (explain|clarify|tell me|break)\b/i,
    /\b(what does (this|that) mean|what do you mean)\b/i,
    /\b(explain (the|this|that) (price|quote|estimate|breakdown))\b/i,
    /\bso much\b/i,
    // ── English — "is X final / does X include / what's included" ────
    /\b(is (that|this|it) final|is the price final|final price)\b/i,
    /\b(does (that|this|it) include|what('?s| is)? included|what does (it|this|that) include)\b/i,
    /\b(is\s+\w+\s+included|are\s+\w+\s+included)\b/i,           // "is oven included", "are bathrooms included"
    /\b(do you (bring|offer|provide|supply|use|need)|will you (bring|provide))\b/i, // "do you bring supplies"
    /\b(do i need to|should i|am i supposed to)\b/i,             // "do I need to be home"
    /\b(how long does (it|this|that)|how long will|how long for)\b/i, // "how long does it take"
    /\b(what time|at what time|when can you|when are you|when would you)\b/i,
    // ── English — "can I / can we / can you ... <verb>" (modal intent
    //    that's almost always a question even without "?")────────────
    /\b(can|could) (i|we)\s+(see|get|have|book|schedule|do|try|use|come)\b/i,
    /\b(can|could) you\s+(do|send|email|text|whatsapp|share|provide|come|swing|fit|squeeze|lower|reduce|drop)\b/i,
    /\b(do|does) you\s+(have|offer|do)\s+(\w+day|tomorrow|tonight|the\s|any|available|availability|openings?|slots?)/i,
    /\b(are you (available|free|open)|is anyone (available|free))\b/i,
    /\b(what about|how about)\s+/i,                              // "what about early morning"
    // ── English — pricing pushback ────────────────────────────────────
    /\b(too expensive|too pricey|too high|too much)\b/i,
    /\b(can you (lower|reduce|drop|come down)|any (discount|deals?|specials?)|cheaper)\b/i,
    /\b(can you do (it|this) for\s*\$?\d)/i,
    /\bbest price\b/i,
    /\b(price\s+range|range\s+of|the\s+range)\b/i,
    /\b(between (\$?\d|the))\b/i,
    // ── English — price objection / concern (NO punctuation, NO price
    //    needed in the message — these are objection signals even when
    //    standalone). Wins over INTEREST/sendReply so the owner addresses
    //    the concern before sending pricing.
    /\b(really\s+)?expensive\b/i,
    /\bcosts?\s+too\s+much\b/i,
    /\b(higher|more)\s+than\s+(i\s+)?(expected|thought|hoped)\b/i,
    /\b(over|outside|out\s+of|not\s+in)\s+(my\s+)?budget\b/i,
    /\bthat'?s\s+(a\s+lot|pricey|expensive|high|steep|too\s+much)\b/i,
    /\bprice\s+(is\s+)?(high|steep|a\s+lot|too\s+high)\b/i,
    /\b(lower|reduce|drop)\s+the\s+price\b/i,
    /\bdiscount\b/i,
    /\bcan\s+you\s+(do|make)\s+(it\s+)?cheaper\b/i,
    /\bcan\s+you\s+work\s+with\s+me\s+on\s+(the\s+)?price\b/i,
    /\bany\s+(deals?|specials?|discounts?)\b/i,
    /\bnegotiat\w*\b/i,
    // ── English — interrogative starters (catches typo'd no-punct Qs)─
    /^\s*(why|what|when|where|who|which|how|whose|whom)\b/i,
    /^\s*(is|are|was|were|do|does|did|can|could|should|would|will|may|might|have|has|had)\s+\w+/i,
    // ── English — quote request without commitment language ──────────
    /\b(can|could) (i|we) (see|get|have)\s+(a |the |my )?(quote|estimate|price|pricing)/i,
    // Direct interrogatives (question marks anywhere) — still useful when present.
    /\?/,

    // ── Spanish — "por qué" and clarification ─────────────────────────
    /\bpor\s*qu[ée](?=\s|[^\w]|$)/i,
    /\b(puedes|podr[íi]as)\s+(explicar|aclarar|decirme|desglosar|hacerlo|bajar|reducir|descontar|mandar(me)?|enviar(me)?|venir|ofrecer|incluir|ajustar)\b/i,
    /\bexpl[íi]came\b/i,
    /\b(qu[ée] significa|qu[ée] quieres decir)\b/i,
    /\b(eso|esto)\s+incluye\b/i,
    /\b(qu[ée]\s+incluye|qu[ée]\s+est[áa]\s+incluido)\b/i,
    // "<noun> está incluido/incluida/s" — works both word orders.
    /\b(est[áa]\s+(el|la|los|las)?\s*\w+\s+incluid[ao]s?)\b/i,
    /\b\w+\s+est[áa]\s+incluid[ao]s?\b/i,
    // "tengo que estar / tengo que tener" — customer-readiness question.
    /\btengo\s+que\s+(estar|tener|recibir|abrir|dejar)\b/i,
    /\b(rango|el\s+rango|entre\s+\$?\d|entre\s+(el|los))\b/i,
    // ── Spanish — pricing pushback ────────────────────────────────────
    /\b(est[áa]\s+(muy\s+)?caro|muy caro|demasiado caro|muy elevado)\b/i,
    /\b(puedes\s+(bajar|reducir|descontar)|alg[úu]n\s+(descuento|especial))\b/i,
    /\b(puedes\s+hacerlo\s+por\s*\$?\d)/i,
    /\bmejor\s+precio\b/i,
    /\b(ese|es)\s+(el\s+)?precio\s+final\b/i,
    // ── Spanish — price objection / concern ───────────────────────────
    /\b(muy\s+costoso|demasiado\s+costoso|muy\s+elevado|precio\s+(muy\s+)?alto)\b/i,
    /\bfuera\s+de\s+(mi\s+)?presupuesto\b/i,
    /\bm[áa]s\s+de\s+lo\s+(que\s+)?(esperaba|esperado|pensaba)\b/i,
    /\b(puedes|podr[íi]as)\s+bajar(\s+el\s+precio)?\b/i,
    /\bdescuento\b/i,
    /\balg[úu]n\s+(descuento|especial|trato)\b/i,
    /\bm[áa]s\s+(barato|econ[óo]mico)\b/i,
    // ── Spanish — availability / scheduling questions w/o "?" ─────────
    /\b(podemos|podr[íi]amos)\s+(hacerlo|verlo|reservar|agendar|ir)\b/i,
    /\b(tienes|tiene|tienen)\s+(disponibilidad|disponible|tiempo|hueco|espacio|cita)\b/i,
    /\b(est[áa]s?\s+disponible|est[áa]n\s+disponibles)\b/i,
    /\b(qu[ée]\s+(d[íi]as?|horas?|tiempo|hora exacta)|a\s+qu[ée]\s+hora)\b/i,
    /\bcu[áa]nto\s+(tarda|cuesta|dura|cobran?|vale|sale)\b/i,
    /\b(traen|usan|necesitan|necesito\s+(estar|tener))\b/i,
    /\b(me\s+puedes|me\s+podr[íi]as)\s+(mandar|enviar|decir|cotizar|dar)\b/i,
    /\b(puedo|podr[íi]a)\s+(ver|tener|recibir)\s+(la\s+|el\s+|una\s+|un\s+)?(cotizaci[óo]n|estimado|precio|presupuesto)\b/i,
    // Spanish interrogatives — inverted question mark anywhere.
    /¿/,
    /^\s*(qu[ée]|cu[áa]ndo|c[óo]mo|d[óo]nde|cu[áa]l|qui[ée]n|cu[áa]nto|cu[áa]nta|por qu[ée])\b/i,
  ];

  const INTEREST_PATTERNS = [
    // ── English ────────────────────────────────────────────────────────
    // Standalone affirmatives (work as a whole message OR phrase fragment).
    /\b(perfect|great|awesome|excellent|wonderful)\b/i,
    /\b(yes|yep|yeah|yup|sure|absolutely|definitely)\b\s*(please)?/i,
    /\b(okay|ok)\b/i,
    /\b(sounds good|sounds great|looks good|looks great)\b/i,
    /\b(go ahead|let'?s do it|let'?s go|do it|i'?m in|i'?ll take it|sign me up)\b/i,
    /\b(schedule it|book it|book me|reserve it|lock it in)\b/i,
    /\b(ready to (book|move forward|schedule|proceed|go)|move forward)\b/i,
    // "<timeframe> is/sounds perfect/great/good" — morning/afternoon/evening/night/tomorrow/weekday.
    /\b(morning|afternoon|evening|night|tonight|tomorrow|today|this (week|weekend)|next (week|weekend)|mon(day)?|tue(sday)?|wed(nesday)?|thu(rsday)?|fri(day)?|sat(urday)?|sun(day)?)\s+(is|sounds|looks|works)\s*(just\s+)?(perfect|great|good|fine|ok(ay)?)\b/i,
    // "that works" / "that time works" / "that is perfect" / "works for me" / "either works".
    /\b(that(\s+(time|day|date))?|it|this)\s+(works|is\s+(perfect|fine|good|great|okay|ok))\b/i,
    /\b(works|fine)\s+for\s+me\b/i,
    /\beither (one )?works\b/i,
    // Scheduling / availability questions.
    /\b(can you do|are you available|do you have|how about|what about) (\w+day|mon|tue|wed|thu|fri|sat|sun|tomorrow|this (week|month)|next (week|month)|\d{1,2}\s?(am|pm))/i,
    // Specific time references — "2pm works", "after 2pm", "before noon", "anytime after".
    /\b\d{1,2}\s?(am|pm|:\d{2})\b\s*(works|good|fine|ok)?/i,
    /\b(after|before|around|by|past)\s+(\d{1,2}(\s?(am|pm|:\d{2}))?|noon|midnight|lunch|dinner|breakfast)\b/i,
    /\banytime\s+(after|before|around|in\s+the)\b/i,
    // "<weekday/timeframe> works" — "Friday works", "tomorrow works", "next week works ok".
    /\b(mon(day)?|tue(sday)?|wed(nesday)?|thu(rsday)?|fri(day)?|sat(urday)?|sun(day)?|tomorrow|this week|next week|this month|next month)\s+(works|good|fine|ok|sounds good)\b/i,
    // Day-part + works/good (e.g. "morning works", "afternoon is good", "tonight works").
    /\b(morning|afternoon|evening|tonight|today)\s+(works|good|fine|ok)\b/i,
    // Pricing intent.
    /\b(what'?s the price|how much|price\??|cost\??|quote\??|estimate\??)/i,
    // Quote delivery / channel intent ("email and text would be great",
    // "send me the quote", "send over the estimate", "please send it").
    /\b(email|text|sms|whatsapp)\s+(works|is fine|is good|is great|would be (great|fine|good)|would work|ok|okay)\b/i,
    /\b(both\s+)?(email|text|sms)\s+(and|&|or)\s+(email|text|sms)\b/i,
    /\b(send|email|text|whatsapp|mail)\s+(me\s+|over\s+|it\s+)?(the\s+)?(quote|estimate|price|details|info|information)\b/i,
    // "send it over" / "send over" — ready-to-receive intent without the noun.
    /\bsend\s+(it\s+over|over)\b/i,
    /\b(send it (by|via|through)\s+(email|text|sms)|you can (email|text) it|please send it)\b/i,

    // ── Spanish ────────────────────────────────────────────────────────
    // Standalone affirmatives.
    /\b(perfecto|perfecta|excelente|genial|estupendo)\b/i,
    // "sí" / "sí por favor" — JS \b is ASCII-only so we use literal
    // start/whitespace anchors that are accent-safe.
    /(^|[\s.,!¡¿?:;])s[íi](\s+por favor)?($|[\s.,!?:;])/i,
    /\b(adelante|hag[áa]moslo|hagamoslo|listo|claro)\b/i,
    /\b(ag[ée]ndalo|agendalo|reserv(a|alo|emos|ame|ámelo)|res[ée]rvalo)\b/i,
    /\b(me funciona|me sirve|funciona para m[íi]|funciona)\b/i,
    /\b(suena bien|me parece bien|me parece perfecto)\b/i,
    // "la tarde/mañana está perfecta/bien" — timeframe + estar + perfecto/bien.
    /\b(la\s+)?(tarde|ma[ñn]ana|noche|madrugada|hoy|ma[ñn]ana|esta\s+(semana|noche)|este\s+(fin\s+de\s+semana|mes))\s+(est[áa]|se ve|suena)\s+(perfect[ao]|bien|genial|excelente)\b/i,
    // "ese horario/día/momento está bien|perfecto".
    /\b(ese|este|esa|esta)\s+(horario|d[íi]a|fecha|momento|tiempo)\s+(est[áa]|se ve|suena|me)\s+(perfect[ao]|bien|funciona|sirve)\b/i,
    /\best[áa]\s+(perfect[ao]|bien|genial)\b/i,
    // Scheduling specifics.
    /\b(despu[ée]s|antes)\s+(de|del)\s+(las?\s+)?(\d{1,2}|mediod[íi]a|medianoche|almuerzo|cena|desayuno)\b/i,
    /\b(cu[áa]nto cuesta|qu[ée] precio|cu[áa]ndo puedes|disponib(le|ilidad))\b/i,
    // Quote delivery / channel intent.
    /\bpor\s+(email|correo|mensaje|texto|sms|whatsapp)\s+(est[áa] bien|me funciona|me sirve|funciona|bien)\b/i,
    /\b(email|correo|texto|sms)\s+(y|o)\s+(email|correo|texto|sms)\b/i,
    /\b(m[áa]ndame|env[íi]ame|env[íi]ele|m[áa]ndamelo|env[íi]amelo|mandamelo|enviamelo)\s+(la\s+|el\s+)?(cotizaci[óo]n|estimado|presupuesto|precio|cotizacion)\b/i,
    /\b(m[áa]ndamelo|env[íi]amelo|mandamelo|enviamelo)\b/i,
    /\b(m[áa]ndamelo|env[íi]amelo)\s+(por|via)\s+(email|correo|texto|sms|whatsapp)\b/i,
  ];

  // Haystack shared by detectQuestion + detectInterest — owner's pasted
  // "Customer's latest reply" textarea takes priority but we also scan
  // the original lead message + internal notes.
  const _haystack = () =>
    `${lead.message || ""}\n${lead.internal_notes || ""}\n${customerContext || ""}`.trim();

  const detectQuestion = () => {
    const h = _haystack();
    if (!h) return false;
    return QUESTION_PATTERNS.some((re) => re.test(h));
  };

  const detectInterest = () => {
    // Scan ALL surfaces where a customer reply might live: the initial
    // `lead.message`, any pasted follow-up in `internal_notes`, and the
    // owner's local "Customer's latest reply" textarea (`customerContext`).
    const haystack = _haystack();
    if (!haystack) return false;
    // Negative wins: if the customer expressed price pushback, hesitation,
    // or disinterest, do NOT surface "Generate Quote" — even if positive
    // keywords also appear in the message.
    if (NEGATIVE_PATTERNS.some((re) => re.test(haystack))) return false;
    return INTEREST_PATTERNS.some((re) => re.test(haystack));
  };

  // ── Follow-up detection ────────────────────────────────────────────
  // When the AI's most recent reply asked a SCHEDULING / TIME question
  // (e.g. Airbnb turnover: "What time does your guest check out?") and
  // the customer's response is a terse time/timeframe answer (e.g.
  // "After 11am", "Around noon", "Guest leaves at 10:30"), those
  // messages are NOT buyer-interest signals — they're continuation of
  // the conversation. We must keep the owner in reply mode so the AI
  // can acknowledge the new info before the quote is offered.
  //
  // CRITICAL: keep AI-pattern set NARROW — only time/scheduling. If we
  // include broad info-gathering patterns ("how many bedrooms", "your
  // address"), every standard reply ever drafted would route terse
  // time replies into followup, breaking the "afternoon is perfect" →
  // interest flow on non-Airbnb leads.
  const AI_SCHEDULING_QUESTION_PATTERNS = [
    // EN — Airbnb turnover + general scheduling
    /\bcheck[\s-]?out\b/i,             // checkout / check out / check-out
    /\bcheck[\s-]?in\b/i,
    /\bturnover\b/i,
    /\bwhat\s+time\b/i,
    /\bat\s+what\s+time\b/i,
    /\bwhen\s+(do|does|will|is|are|would)\s+(the\s+|your\s+)?(guest|guests|tenant|client|customer|cleaning|booking|appointment|service)\b/i,
    /\bguest\s+(leave|leaves|check|checks|out|depart|departs|arrival|arrives)\b/i,
    /\b(preferred|best|good)\s+time\b/i,
    /\bwhat'?s\s+(a\s+)?good\s+time\b/i,
    // "Would you prefer mornings or afternoons?" / "morning or afternoon"
    // — explicit day-part choice prompts. These were missing previously
    // so a customer's "Let's do mornings" never routed into followup.
    /\b(morning|mornings|afternoon|afternoons|evening|evenings)\s+or\s+(morning|mornings|afternoon|afternoons|evening|evenings|night|nights)\b/i,
    /\b(prefer|like|want|do\s+you\s+want|would\s+you\s+like)\s+.{0,30}\b(morning|mornings|afternoon|afternoons|evening|evenings)\b/i,
    /\b(am|a\.m\.?|pm|p\.m\.?)\s+or\s+(am|a\.m\.?|pm|p\.m\.?)\b/i,
    // ES
    /\b(a\s+)?qu[ée]\s+hora\b/i,
    /\bcu[áa]ndo\s+(sale|se\s+va|deja|llega|termina)/i,
    /\bhu[ée]sped/i,
    /\bhora\s+de\s+(salida|entrada|llegada)\b/i,
    /\bsalida\s+del\s+hu[ée]sped/i,
    /\b(ma[ñn]ana|ma[ñn]anas|tarde|tardes|noche|noches)\s+o\s+(ma[ñn]ana|ma[ñn]anas|tarde|tardes|noche|noches)\b/i,
    /\b(prefiere|prefieres|quieres?|le\s+gusta(r[ií]a)?)\s+.{0,30}\b(ma[ñn]ana|tarde|noche)\b/i,
  ];
  const TERSE_TIME_REPLY_PATTERNS = [
    // Bare time references: "11am", "10:30", "2 PM"
    /\b\d{1,2}\s*(am|pm|:\d{2})/i,
    // Day-parts that are answers, not buyer enthusiasm. Includes plurals
    // ("mornings"/"afternoons") because customers commonly reply with
    // them: "Let's do mornings", "Mornings work for me", "Afternoons
    // are best". Without plurals these terse confirmations slipped
    // through and the next-action card stayed on sendReply.
    /\b(noon|midnight|midday|mornings?|afternoons?|evenings?|nights?|tonight|today|tomorrow|early|late)\b/i,
    // "Let's do X", "X works", "X is fine/perfect/good for me", "anytime in the X"
    /\b(let'?s\s+do|do)\s+(the\s+)?(mornings?|afternoons?|evenings?|nights?)\b/i,
    /\b(mornings?|afternoons?|evenings?|nights?)\s+(work|works|is|are)\s+(for\s+me|fine|good|great|perfect|ok|best)\b/i,
    /\b(anytime|sometime|any\s+time)\s+in\s+the\s+(mornings?|afternoons?|evenings?)\b/i,
    // "after 11am", "before noon", "around 2pm", "by lunch", "past midnight"
    /\b(after|before|around|by|past|about|approximately|sometime)\s+(\d{1,2}|noon|midnight|lunch|breakfast|dinner|midday)\b/i,
    // "Guest leaves at 10:30", "leaves around noon" — info-bearing
    /\b(leaves?|departs?|arrives?|checks?\s+(in|out))\s+(at|around|by|before|after|sometime)?\s*(\d{1,2}|noon|midnight)/i,
    // ES counterparts
    /\b(despu[ée]s|antes|alrededor|por)\s+(de\s+las?\s+)?(\d{1,2}|mediod[íi]a|medianoche)\b/i,
    /\b(sale|deja|llega|se\s+va)\s+(a|alrededor|sobre|por|antes|despu[ée]s)?\s*(las?\s+)?(\d{1,2}|mediod[íi]a|medianoche)/i,
    /\bmediod[íi]a\b/i,
    /\b(ma[ñn]ana|ma[ñn]anas|tarde|tardes|noche|noches|madrugada)\b/i,
    /\b(hag[áa]moslo|hagamos)\s+(en\s+la(s)?\s+)?(ma[ñn]ana|tarde|noche)/i,
  ];
  const detectFollowup = () => {
    const ctx = (customerContext || "").trim();
    if (!ctx) return false;
    // Only short replies (~12 words / 80 chars) — anything longer is
    // probably a real sentence-shaped message, not a terse answer.
    if (ctx.length > 80) return false;
    const ai = (lead.ai_reply || "").trim();
    if (!ai) return false;
    const aiAsked = AI_SCHEDULING_QUESTION_PATTERNS.some((re) => re.test(ai));
    if (!aiAsked) return false;
    return TERSE_TIME_REPLY_PATTERNS.some((re) => re.test(ctx));
  };

  // Returns { id, ctaKey, onClick } so the UI can render a single card
  // with the right copy and a single primary button. `id` doubles as the
  // i18n key root: `lead.nextAction.<id>.title` / `.body` / `.cta`.
  //
  // STALE-REPLY CONTRACT: When the owner pastes a new customer message
  // into the "Customer's latest reply" textarea, the previously drafted
  // AI reply becomes stale relative to that new context. The card must
  // therefore re-classify intent on the new context and never lock on
  // "sendReply" — that branch only applies when the current draft was
  // produced for the current customer context.
  const recommendedNextAction = (() => {
    const isAccepted = lead.status === "booked";
    const isClosed = lead.status === "closed";
    if (isClosed) {
      return {
        id: "review",
        onClick: requestReview,
        disabled: requestingReview,
      };
    }
    if (isAccepted) {
      return {
        id: "booked",
        onClick: () => navigate(`/calendar?lead=${id}`),
      };
    }
    if (step3Done) {
      // Quote link exists but not accepted yet — waiting state, no CTA.
      return { id: "waiting" };
    }
    if (step2Done) {
      // Estimate ready, no public link yet — send the quote.
      return {
        id: "sendQuote",
        onClick: () => quoteLinkCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      };
    }
    // Stale-reply detection: the current AI draft was generated for the
    // customer context value held in `lastAnsweredContext`. If the owner
    // has since pasted a different customer reply, the draft no longer
    // matches reality — every branch below that depends on the draft
    // being current must regenerate first.
    const ctxNow = (customerContext || "").trim();
    const ctxAns = (lastAnsweredContext || "").trim();
    const hasNewCustomerReply = ctxNow.length > 0 && ctxNow !== ctxAns;
    if (step1Done && detectQuestion()) {
      // Customer asked a question or pushed back on price — owner MUST
      // reply first, not jump to generating a quote. Wins over interest.
      // Yields to sendReply only when the current draft already answers
      // the current context.
      if (hasNewCustomerReply || ctxNow !== ctxAns) {
        return {
          id: "question",
          onClick: scrollAndGenReply,
          disabled: generatingReply,
        };
      }
    }
    if (step1Done && detectFollowup()) {
      // Customer answered a scheduling/info question. Keep the owner in
      // reply mode so the AI can acknowledge the new info. Same stale
      // check — only flips to sendReply once the draft was regenerated
      // for the SAME context value.
      if (hasNewCustomerReply || ctxNow !== ctxAns) {
        return {
          id: "followup",
          onClick: scrollAndGenReply,
          disabled: generatingReply,
        };
      }
    }
    if (step1Done && detectInterest()) {
      // Reply ready AND customer signalled interest — push for the quote.
      // Interest signals should be acted on even when there's a fresh
      // customer reply (the new reply is the interest signal itself).
      return {
        id: "interest",
        onClick: scrollAndGenQuote,
        disabled: generatingQuote,
      };
    }
    if (step1Done) {
      // If the owner pasted a new customer reply that didn't trigger
      // question/followup/interest classification, the AI draft is still
      // stale relative to that reply — regenerate before sending.
      if (hasNewCustomerReply) {
        return {
          id: "generateReply",
          onClick: scrollAndGenReply,
          disabled: generatingReply,
        };
      }
      // Reply ready, no new customer context — copy & send the reply.
      return {
        id: "sendReply",
        onClick: () => copyText(replyDraft, "reply", t("lead.replyLabel")),
      };
    }
    // Default: no reply yet — generate one.
    return {
      id: "generateReply",
      onClick: scrollAndGenReply,
      disabled: generatingReply,
    };
  })();

  const infoRow = (Icon, label, value, testid) => (
    <div className="flex items-start gap-3 py-3" data-testid={testid}>
      <Icon className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm text-gray-900 break-words">
          {value || <span className="text-gray-400 italic">{t("lead.notProvided")}</span>}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f9f9f8]" data-testid="lead-detail-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> {t("lead.backToDashboard")}
        </Link>

        <div className="mt-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900">{lead.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <StatusBadge status={lead.status} />
              {lead.returning_customer && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-800 border border-green-200"
                  data-testid="returning-customer-pill"
                >
                  <Repeat className="w-3 h-3" />
                  Returning customer from previous cleaning
                </span>
              )}
              <span className="text-xs text-gray-400">Added {new Date(lead.created_at).toLocaleString()}</span>
              <span className="text-xs text-gray-400 capitalize">Source: {lead.source?.replace("_", " ")}</span>
              {lead.discovery_source === "find_a_cleaner" && (
                <span className="text-[11px] font-medium text-green-800 bg-green-50 border border-green-100 rounded-full px-2 py-0.5" data-testid="lead-via-find-cleaner-badge">
                  {t("lead.viaFindACleaner")}
                </span>
              )}
              {lead.returning_customer && lead.previous_lead_id && (
                <Link
                  to={`/leads/${lead.previous_lead_id}`}
                  className="text-xs text-green-800 hover:underline inline-flex items-center gap-1"
                  data-testid="previous-lead-link"
                  title="Open the customer's previous lead"
                >
                  <Link2 className="w-3 h-3" />
                  Previous lead linked
                </Link>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end gap-1.5">
              <Select value={lead.status} onValueChange={updateStatus}>
                <SelectTrigger className="h-10 w-36" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {t(`lead.status${s.charAt(0).toUpperCase()}${s.slice(1)}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-400 leading-tight" data-testid="status-helper">
                {t("lead.statusHelper")}
              </p>
              {/* Status-specific clarity line — tells the owner what each
                  CRM stage means for revenue / pipeline math. Helps reinforce
                  the new "closed = revenue, booked = pipeline" model. */}
              {lead.status === "closed" && (
                <p className="text-[11px] text-emerald-700 leading-tight font-medium" data-testid="status-closed-helper">
                  {t("lead.statusClosedHelper")}
                </p>
              )}
              {lead.status === "booked" && (
                <p className="text-[11px] text-blue-700 leading-tight font-medium" data-testid="status-booked-helper">
                  {t("lead.statusBookedHelper")}
                </p>
              )}
              {/* Cancel-reason dropdown — only when the lead is currently
                  cancelled. Pre-fills with the stored reason. Selecting a
                  new value PATCHes the lead, never blocks the status
                  select. Lives next to the status to keep both lifecycle
                  decisions visually grouped. */}
              {lead.status === "cancelled" && (
                <div className="w-44" data-testid="cancel-reason-wrap">
                  <Select
                    value={lead.cancel_reason || ""}
                    onValueChange={updateCancelReason}
                  >
                    <SelectTrigger
                      className="h-9 w-full text-[12px]"
                      data-testid="cancel-reason-select"
                    >
                      <SelectValue placeholder={t("lead.cancelReason.placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {CANCEL_REASONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {t(`lead.cancelReason.${r}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-gray-400 leading-tight mt-1">
                    {t("lead.cancelReason.label")}
                  </p>
                </div>
              )}
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 text-amber-700 hover:text-amber-800"
                  data-testid="archive-lead-button"
                  title={t("lead.archive")}
                >
                  <Archive className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("lead.archive")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("lead.archiveConfirm")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={archiveLead}
                    data-testid="confirm-archive"
                  >
                    {t("lead.archive")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {/* Permanent delete — platform admins ONLY. The backend also
                gates this with a 403, but hiding the button keeps the UI
                honest for normal owners. */}
            {user?.is_admin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-10 text-red-600 hover:text-red-700"
                    data-testid="delete-lead-button"
                    title={t("lead.permanentDelete")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("lead.permanentDelete")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("lead.permanentDeleteConfirm")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={permanentDeleteLead}
                      data-testid="confirm-delete"
                    >
                      {t("lead.permanentDelete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Cancelled banner — gentle warning + cancellation date when the
            lead is in the "cancelled" state. Sits below the header so the
            user sees the consequence immediately after switching status. */}
        {lead.status === "cancelled" && (
          <div
            className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            data-testid="cancelled-banner"
          >
            <div className="font-semibold">{t("lead.statusCancelled")}</div>
            <p className="mt-0.5 text-[13px] leading-relaxed">
              {t("lead.cancelledWarning")}
            </p>
            {lead.cancelled_at && (
              <p className="mt-1 text-[11px] text-red-700/80">
                {t("lead.cancelledOn")}{" "}
                {new Date(lead.cancelled_at).toLocaleDateString()}
              </p>
            )}
          </div>
        )}

        {/* Paywall — trial expired & not subscribed */}
        {locked && (
          <div
            className="mt-8 rounded-2xl bg-white border border-gray-200 p-8 text-center shadow-sm"
            data-testid="lead-paywall-card"
          >
            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 mx-auto flex items-center justify-center">
              <Lock className="w-6 h-6 text-amber-700" />
            </div>
            <h2
              className="mt-5 font-display text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight"
              data-testid="lead-paywall-title"
            >
              {t("lead.paywallTitle")}
            </h2>
            <p className="mt-3 text-gray-600 max-w-md mx-auto" data-testid="lead-paywall-text">
              {t("lead.paywallBody")}
            </p>
            <Button
              onClick={() => navigate("/pricing#pricing")}
              className="mt-6 h-12 px-7 bg-green-800 hover:bg-green-900 text-white text-base font-semibold shadow-md"
              data-testid="lead-paywall-cta"
            >
              {t("lead.paywallCta")}
            </Button>
            <p className="mt-3 text-xs text-gray-500" data-testid="lead-paywall-trust">
              {t("lead.paywallTrust")}
            </p>
          </div>
        )}

        {/* Missed lead alert */}
        {!locked && isMissedLead(lead) && (
          <div
            className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3"
            data-testid="lead-missed-alert"
          >
            <span className="w-9 h-9 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
            </span>
            <div>
              <p className="font-medium text-amber-900">
                {t("lead.missedTitle")}
              </p>
              <p className="text-sm text-amber-800/80 mt-0.5">
                {t("lead.missedBody", formatMissedDuration(lead))}
              </p>
            </div>
          </div>
        )}

        {/* Quick action bar */}
        {!locked && (
          <>
        {/* 3-step closer guide — hidden on mobile (<md). On phones the
            Step 1/2/3 chip headers already convey the workflow, so this
            instructional block was duplicated noise. Visible from md+. */}
        <div className="mt-6 hidden md:block" data-testid="closer-guide">
          <p className="font-display font-semibold text-gray-800">
            {t("lead.closeIn3Steps")}
          </p>
          <ol className="mt-2 text-sm text-gray-600 space-y-1 list-decimal list-inside marker:text-gray-400 marker:font-semibold">
            <li data-testid="closer-step-1">{t("lead.closerStep1")}</li>
            <li data-testid="closer-step-2">{t("lead.closerStep2")}</li>
            <li data-testid="closer-step-3">{t("lead.closerStep3")}</li>
          </ol>
          <p className="mt-2 text-xs text-gray-500 leading-relaxed" data-testid="closer-helper">
            {t("lead.closeIn3StepsHelp")}
          </p>
        </div>

        {/* Quick action bar — hidden on mobile here (rendered inside the
            grid wrapper below, positioned right after Step 1 so users
            can mark Contacted / Quoted only after drafting a reply). */}
        <div className="mt-6 hidden md:flex flex-wrap items-center gap-2" data-testid="quick-actions">
          <Button
            variant="outline"
            onClick={() => quickStatus("contacted", setMarkingContacted)}
            disabled={markingContacted || lead.status === "contacted"}
            className="h-9 bg-white"
            data-testid="mark-contacted-button"
          >
            {markingContacted ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <MailCheck className="w-3.5 h-3.5 mr-1.5" />}
            {t("lead.markContacted")}
          </Button>
          <Button
            variant="outline"
            onClick={() => quickStatus("quoted", setMarkingQuoted)}
            disabled={markingQuoted || lead.status === "quoted"}
            className="h-9 bg-white"
            data-testid="mark-quoted-button"
          >
            {markingQuoted ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <BadgeDollarSign className="w-3.5 h-3.5 mr-1.5" />}
            {t("lead.markQuoted")}
          </Button>
          <Button
            variant="outline"
            onClick={copyBoth}
            disabled={!replyDraft?.trim() && !quoteDraft?.trim()}
            className="h-9 bg-white"
            data-testid="copy-reply-quote-button"
          >
            {copied.both ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <FilePlus className="w-3.5 h-3.5 mr-1.5" />}
            {t("lead.copyReplyQuote")}
          </Button>
          <Button
            variant="outline"
            onClick={openCalendar}
            className="h-9 bg-white"
            data-testid="add-to-calendar-button"
          >
            <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
            {t("lead.addToCalendar")}
          </Button>
          {/* Google Review Request — only meaningful after a job is booked or
              completed, the customer left an email, and the owner has saved
              their Google review URL in Settings. Disabled (with explanatory
              copy) otherwise so the action is discoverable regardless. */}
          {(lead.status === "booked" || lead.status === "closed") && (() => {
            const hasReviewLink = Boolean((user?.google_review_link || "").trim());
            const hasCustomerEmail = Boolean((lead.email || "").trim());
            // Hide entirely if backend masked the email (paywall) so we never
            // promise an email-based action we can't actually perform.
            if (lead.email && /^\w?\*\*\*@/.test(lead.email)) return null;
            const disabledReason = !hasCustomerEmail
              ? "Customer email required"
              : !hasReviewLink
              ? "Add Google review link in Settings"
              : null;
            return (
              <div className="inline-flex items-center" data-testid="review-button-group">
                <Button
                  variant="outline"
                  onClick={requestReview}
                  disabled={Boolean(disabledReason) || requestingReview}
                  title={disabledReason || ""}
                  className="h-9 bg-white"
                  data-testid="request-review-button"
                >
                  {requestingReview ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Star className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {disabledReason || "Request Google Review"}
                </Button>
                {/* Preview only — opening this never triggers a send. The
                    eye icon is hidden when the action itself is disabled
                    because the preview would otherwise mislead — there's
                    no review link to show without one saved in Settings. */}
                {!disabledReason && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReviewPreviewOpen(true)}
                    className="h-9 px-2 ml-0.5 text-gray-500 hover:text-gray-800"
                    aria-label="Preview Google review request email"
                    title="Preview email"
                    data-testid="preview-review-button"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            );
          })()}
          {/* Rebook Reminder — same status gate as review, plus an age check:
              only shows once the lead is at least 30 days old (i.e. enough
              time has passed since the cleaning that another one is due).
              Hidden when the customer email is paywall-masked. */}
          {(lead.status === "booked" || lead.status === "closed") && (() => {
            if (lead.email && /^\w?\*\*\*@/.test(lead.email)) return null;
            const hasCustomerEmail = Boolean((lead.email || "").trim());
            const ageDays = lead.created_at
              ? Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 86400000)
              : 0;
            const isOldEnough = ageDays >= 30;
            // Cooldown gate (UI-side mirror of the backend 30-day rule). The
            // backend is still the source of truth — this just keeps the UI
            // honest if the page hasn't been reloaded.
            const cooldownActive =
              lead.rebook_followup_sent_at &&
              Date.now() - new Date(lead.rebook_followup_sent_at).getTime() <
                30 * 86400000;
            if (!isOldEnough && !lead.rebook_followup_sent_at) return null;
            const disabledReason = !hasCustomerEmail
              ? "Customer email required"
              : cooldownActive
              ? "Follow-up already sent recently"
              : null;
            return (
              <div className="inline-flex items-center" data-testid="rebook-button-group">
                <Button
                  variant="outline"
                  onClick={sendRebookFollowup}
                  disabled={Boolean(disabledReason) || sendingRebook}
                  title={disabledReason || ""}
                  className="h-9 bg-white"
                  data-testid="send-rebook-button"
                >
                  {sendingRebook ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Repeat className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {disabledReason || "Send Rebook Reminder"}
                </Button>
                {/* Preview only — opening this never triggers a send. */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRebookPreviewOpen(true)}
                  className="h-9 px-2 ml-0.5 text-gray-500 hover:text-gray-800"
                  aria-label="Preview rebook reminder email"
                  title="Preview email"
                  data-testid="preview-rebook-button"
                >
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              </div>
            );
          })()}
        </div>

        {lead.review_request_sent_at ? (
          <p
            className="hidden md:inline-flex mt-2 text-xs text-gray-500 items-center gap-1.5"
            data-testid="review-request-sent-badge"
          >
            <Star className="w-3 h-3 text-green-700" />
            Review requested on{" "}
            {new Date(lead.review_request_sent_at).toLocaleDateString(undefined, {
              year: "numeric", month: "short", day: "numeric",
            })}
          </p>
        ) : null}
        {lead.rebook_followup_sent_at ? (
          <p
            className="hidden md:inline-flex mt-1 text-xs text-gray-500 items-center gap-1.5"
            data-testid="rebook-sent-badge"
          >
            <Repeat className="w-3 h-3 text-green-700" />
            Rebook reminder sent on{" "}
            {new Date(lead.rebook_followup_sent_at).toLocaleDateString(undefined, {
              year: "numeric", month: "short", day: "numeric",
            })}
          </p>
        ) : null}

        {/* Layout — mobile uses a flat flex-col so each card can be
            individually re-ordered via `order-*` (workflow first, lead
            details collapsed at the bottom). On `lg:` we swap to the
            classic 3-column grid via `lg:block` on the contents-wrappers
            and explicit `lg:col-span-*` to restore the desktop look. */}
        <div className="mt-4 md:mt-6 flex flex-col gap-3 lg:grid lg:grid-cols-3 lg:gap-5 lg:items-start">
          {/* Left column: Score + Lead info + Notes */}
          <div className="contents lg:block lg:col-span-1 lg:space-y-5 min-w-0">
            <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6 order-10 md:order-3 lg:order-none" data-testid="lead-score-card">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="font-display font-medium text-gray-900">{t("lead.scoreTitle")}</p>
                    <p className="text-xs text-gray-500">{t("lead.scoreSubtitle")}</p>
                  </div>
                </div>
                {lead.lead_score_label && (
                  <button
                    onClick={analyzeScore}
                    disabled={analyzingScore}
                    title="Re-analyze"
                    className="text-gray-400 hover:text-gray-700 transition-colors p-1.5 disabled:opacity-50"
                    data-testid="rescore-button"
                  >
                    {analyzingScore ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                  </button>
                )}
              </div>

              {lead.lead_score_label ? (
                <div className="mt-5">
                  <div className="flex items-center gap-3" data-testid="lead-score-summary">
                    <LeadScoreBadge label={lead.lead_score_label} size="lg" />
                    <span className="font-display text-2xl font-semibold text-gray-900" data-testid="lead-win-probability">
                      {typeof lead.win_probability === "number" ? `${lead.win_probability}%` : "—"}
                    </span>
                    <span className="text-xs text-gray-400 self-end pb-1">{t("lead.winProbability")}</span>
                  </div>
                  {lead.lead_score_reason && (
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed" data-testid="lead-score-reason">
                      {lead.lead_score_reason}
                    </p>
                  )}
                </div>
              ) : (
                <div className="mt-5">
                  <p className="text-sm text-gray-500" data-testid="lead-score-not-analyzed">
                    {t("lead.scoreNotAnalyzed")}
                  </p>
                  <Button
                    onClick={analyzeScore}
                    disabled={analyzingScore}
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full"
                    data-testid="analyze-score-button"
                  >
                    {analyzingScore ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {t("lead.scoreThisLead")}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Recommended Next Action — single-CTA guidance card placed
                directly under Lead Score so the next step is always
                visible without scrolling. The engine above picks ONE
                action per state — never competing actions. */}
            <div
              className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-3.5 md:p-5 order-1 md:order-2 lg:order-none"
              data-testid="next-action-card"
            >
              <div className="flex items-start gap-2.5 md:gap-3">
                <div
                  className="shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center"
                  aria-hidden="true"
                >
                  <Sparkles className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[10px] md:text-[11px] tracking-[0.2em] uppercase font-semibold text-emerald-700"
                    data-testid="next-action-eyebrow"
                  >
                    {t("lead.nextAction.eyebrow")}
                  </p>
                  <p
                    className="mt-0.5 font-display font-semibold text-gray-900 text-[15px] md:text-base leading-snug"
                    data-testid={`next-action-title-${recommendedNextAction.id}`}
                  >
                    {t(`lead.nextAction.${recommendedNextAction.id}.title`)}
                  </p>
                  <p
                    className="mt-1 text-xs md:text-sm text-emerald-900/80 leading-relaxed"
                    data-testid="next-action-body"
                  >
                    {t(`lead.nextAction.${recommendedNextAction.id}.body`)}
                  </p>
                  {recommendedNextAction.onClick ? (
                    <Button
                      size="sm"
                      onClick={recommendedNextAction.onClick}
                      disabled={!!recommendedNextAction.disabled}
                      className="mt-2.5 md:mt-3 bg-emerald-700 hover:bg-emerald-800 text-white h-9 disabled:opacity-60"
                      data-testid="next-action-cta"
                    >
                      {t(`lead.nextAction.${recommendedNextAction.id}.cta`)}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Lead Info card — order-2 on mobile so the customer's
                submitted request sits directly under the Recommended
                Next Action card. Owners tapping the new-lead SMS link
                on mobile should see who the customer is + what they
                requested BEFORE the AI reply / quote workflow, not
                after scrolling past everything. */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6 order-2 lg:order-none" data-testid="lead-info-card">
              {/* Header — acts as a collapse toggle on mobile (<md). On
                  desktop the body is always visible so the toggle is a
                  no-op label (pointer-events-none + cursor-default). */}
              <button
                type="button"
                onClick={() => setLeadDetailsOpen((o) => !o)}
                aria-expanded={leadDetailsOpen}
                aria-controls="lead-info-body"
                className="flex w-full items-center justify-between gap-3 md:cursor-default md:pointer-events-none"
                data-testid="lead-info-toggle"
              >
                <p className="font-display font-medium text-gray-900">{t("lead.detailsTitle")}</p>
                <ChevronDown
                  className={`md:hidden w-4 h-4 text-gray-500 transition-transform ${leadDetailsOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
              <div
                id="lead-info-body"
                className={`${leadDetailsOpen ? "block" : "hidden"} md:block`}
              >
                <div className="mt-3 divide-y divide-gray-100">
                {infoRow(Mail, t("lead.fieldEmail"), lead.email)}
                {infoRow(Phone, t("lead.fieldPhone"), lead.phone)}
                {infoRow(MapPin, t("lead.fieldAddress"), lead.address)}
                {/* Home Size row — suppressed entirely when the field is
                    null/empty/"-" so the lead overview never shows a
                    half-finished "Home size: —" placeholder. New leads
                    use service_details (bedrooms / bathrooms / sqft) as
                    the source of truth; only legacy manually-entered
                    leads still carry a free-form home_size string. */}
                {lead.home_size && String(lead.home_size).trim() && String(lead.home_size).trim() !== "-"
                  ? infoRow(Home, t("lead.fieldHomeSize"), lead.home_size)
                  : null}
                {infoRow(FileText, t("lead.fieldService"), lead.service_type)}
                {(() => {
                  // Show the canonical recurring cadence ABOVE the
                  // preferred-date row. Source-of-truth priority:
                  // explicit `lead.frequency` (owner-edited) → public
                  // form `service_details.frequency`. Hidden when neither
                  // is set so older leads don't render an empty pill.
                  const fRaw =
                    (lead.frequency || lead?.service_details?.frequency || "")
                      .toString()
                      .trim()
                      .toLowerCase();
                  if (!fRaw) return null;
                  const labelKey = `lead.fieldFrequencyValue.${fRaw}`;
                  const labelTry = t(labelKey);
                  const label = labelTry.startsWith("lead.fieldFrequencyValue.")
                    ? fRaw.replace(/_/g, "-").replace(/\b\w/g, (c) => c.toUpperCase())
                    : labelTry;
                  return infoRow(Repeat, t("lead.fieldFrequency"), label, "lead-field-frequency");
                })()}
                {infoRow(Calendar, t("lead.fieldPreferredDate"), lead.preferred_date)}
                {/* Source + Date submitted — surfaced inside the Details
                    card too (in addition to the header pills) so an
                    owner arriving from the SMS link can see the full
                    audit trail without scrolling back up. */}
                {(() => {
                  const src = (lead.source || "").toString().trim();
                  let label = src
                    ? src.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                    : "";
                  if (lead.discovery_source === "find_a_cleaner") {
                    label = label
                      ? `${label} · ${t("lead.viaFindACleaner")}`
                      : t("lead.viaFindACleaner");
                  }
                  return infoRow(Tag, t("lead.fieldSource"), label, "lead-field-source");
                })()}
                {(() => {
                  const raw = lead.created_at;
                  let label = "";
                  try {
                    label = raw ? new Date(raw).toLocaleString() : "";
                  } catch {
                    label = "";
                  }
                  return infoRow(Clock, t("lead.fieldSubmittedAt"), label, "lead-field-submitted-at");
                })()}
              </div>
              {lead.service_details && Object.keys(lead.service_details).length > 0 ? (
                <div className="mt-4 pt-4 border-t border-gray-100" data-testid="lead-service-details">
                  <p className="text-xs text-gray-400">{t("lead.serviceDetailsTitle")}</p>
                  <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    {Object.entries(lead.service_details).map(([k, v]) => {
                      const label = t(`lead.sd.${k}`);
                      // If no translation key exists, fall back to a
                      // humanised version of the raw key so unexpected
                      // keys never break the panel.
                      const display = label.startsWith("lead.sd.")
                        ? k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                        : label;
                      let valDisplay;
                      if (typeof v === "boolean") {
                        valDisplay = v ? t("public.form.yes") : t("public.form.no");
                      } else if (typeof v === "number") {
                        valDisplay = String(v);
                      } else if (typeof v === "string") {
                        // Try translating enum values (frequency, condition,
                        // business_type, construction_type). Fallback to raw
                        // string for unknown values.
                        const enumKey = `lead.sd.val.${v}`;
                        const enumLabel = t(enumKey);
                        valDisplay = enumLabel.startsWith("lead.sd.val.")
                          ? v.replace(/_/g, " ")
                          : enumLabel;
                      } else {
                        valDisplay = String(v);
                      }
                      return (
                        <React.Fragment key={k}>
                          <dt className="text-gray-500" data-testid={`sd-label-${k}`}>{display}</dt>
                          <dd className="text-gray-900 font-medium" data-testid={`sd-value-${k}`}>{valDisplay}</dd>
                        </React.Fragment>
                      );
                    })}
                  </dl>
                </div>
              ) : null}
              {lead.message && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-xs text-gray-400">{t("lead.theirMessage")}</p>
                    <button
                      type="button"
                      onClick={translateCustomerMessage}
                      disabled={translatingMessage}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-green-800 hover:text-green-900 disabled:opacity-50"
                      data-testid="translate-message-button"
                    >
                      {translatingMessage ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Languages className="w-3 h-3" />
                      )}
                      {showTranslated
                        ? t("lead.showOriginal")
                        : (() => {
                            const detected = guessLanguageFromText(lead.message || "");
                            return detected === "es" ? t("lead.translateToEnglish") : t("lead.translateToSpanish");
                          })()}
                    </button>
                  </div>
                  <p className="mt-1.5 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid="lead-message-text">
                    {showTranslated && translatedMessage ? translatedMessage : lead.message}
                  </p>
                  {showTranslated && translatedMessage ? (
                    <p className="mt-1 text-[11px] text-gray-400 italic" data-testid="translated-message-note">
                      {t("lead.translatedNote")}
                    </p>
                  ) : null}
                </div>
              )}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6 order-8 lg:order-none" data-testid="job-value-card">
              <p className="font-display font-medium text-gray-900">
                {t("lead.jobValueTitle")}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                {t("lead.jobValueHelper")}
              </p>
              <div className="mt-3 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none select-none">
                  $
                </span>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={jobValueInput}
                  onChange={(e) => setJobValueInput(e.target.value)}
                  onBlur={saveJobValue}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                  placeholder="0.00"
                  className="pl-7 h-10"
                  disabled={savingJobValue}
                  data-testid="job-value-input"
                />
              </div>
              {lead.booked_at ? (
                <p
                  className="mt-2 text-[11px] text-emerald-700"
                  data-testid="job-value-booked-at"
                >
                  {t("lead.jobValueBookedAt")}{" "}
                  {new Date(lead.booked_at).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              ) : null}
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6 order-9 lg:order-none" data-testid="notes-card">
              <p className="font-display font-medium text-gray-900">{t("lead.notesTitle")}</p>
              <Textarea
                rows={5}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("lead.notesPlaceholder")}
                className="mt-3"
                data-testid="notes-textarea"
              />
              <Button
                onClick={saveNotes}
                disabled={savingNotes || !notesDirty}
                variant="outline"
                className="mt-3 w-full"
                data-testid="save-notes-button"
              >
                {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : notesDirty ? t("lead.saveNotes") : t("lead.saved")}
              </Button>
            </div>
          </div>

          {/* Right column: AI Reply + AI Quote */}
          <div className="contents lg:block lg:col-span-2 lg:space-y-5 min-w-0">
            <div ref={aiReplyCardRef} className={`rounded-2xl ai-card p-4 md:p-6 order-2 md:order-1 lg:order-none ${step1Active ? stepRing : ""}`} data-testid="ai-reply-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <StepChip n={1} state={chipState(step1Active, step1Done)} />
                  <div className="w-10 h-10 rounded-lg bg-white border border-green-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-green-800" />
                  </div>
                  <div>
                    <p className="font-display font-medium text-gray-900">{t("lead.aiReplyTitle")}</p>
                    <p className="text-xs text-gray-500">{t("lead.aiReplySubtitle")}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <Select value={replyLang} onValueChange={setReplyLang}>
                    <SelectTrigger className="h-9 w-[120px] bg-white" data-testid="reply-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">{LANGUAGE_LABELS.en}</SelectItem>
                      <SelectItem value="es">{LANGUAGE_LABELS.es}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyText(replyDraft, "reply", "Reply")}
                    disabled={!replyDraft?.trim()}
                    className="bg-white"
                    data-testid="copy-reply-button"
                  >
                    {copied.reply ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                    {t("lead.copyReply")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={genReply}
                    disabled={generatingReply}
                    className="bg-green-800 hover:bg-green-900 text-white"
                    data-testid="generate-reply-button"
                  >
                    {generatingReply ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (lead.ai_reply ? t("common.regenerate") : t("lead.generateReply"))}
                  </Button>
                </div>
              </div>
              <div className="mt-3" data-testid="reply-helper-text">
                <p className="text-sm font-medium text-gray-800">{t("lead.aiReplyHelper")}</p>
                <p className="hidden md:block mt-0.5 text-xs text-gray-500">{t("lead.aiReplyHelperSub")}</p>
              </div>

              {/* AI v2 controls: tone + optional customer reply context.
                  Stacked on tablet portrait (<lg) so the textarea uses
                  the full available width. Desktop (lg+) keeps the
                  tone-select / context split at 4/8 columns. */}
              <div className="mt-4 grid gap-3 lg:grid-cols-12 items-start" data-testid="reply-controls">
                <div className="lg:col-span-4">
                  <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                    {t("lead.replyToneLabel")}
                  </label>
                  <Select value={replyTone} onValueChange={setReplyTone}>
                    <SelectTrigger className="mt-1.5 h-9 bg-white" data-testid="reply-tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="friendly">{t("lead.toneFriendly")}</SelectItem>
                      <SelectItem value="professional">{t("lead.toneProfessional")}</SelectItem>
                      <SelectItem value="premium">{t("lead.tonePremium")}</SelectItem>
                      <SelectItem value="fast_closing">{t("lead.toneFastClosing")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="lg:col-span-8">
                  <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                    {t("lead.customerContextLabel")}
                  </label>
                  <Textarea
                    rows={2}
                    value={customerContext}
                    onChange={(e) => setCustomerContext(e.target.value)}
                    placeholder={t("lead.customerContextPlaceholder")}
                    className="mt-1.5 bg-white text-sm"
                    data-testid="reply-customer-context"
                    maxLength={2000}
                  />
                  <p className="hidden md:block mt-1 text-[11px] text-gray-500 leading-snug">
                    {t("lead.customerContextHelper")}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="reply-customer-context-examples">
                    <span className="text-[11px] font-medium text-gray-500">
                      {t("lead.customerContextExamplesLabel")}
                    </span>
                    {[
                      t("lead.customerContextEx1"),
                      t("lead.customerContextEx2"),
                      t("lead.customerContextEx3"),
                      t("lead.customerContextEx4"),
                    ].map((ex, i) => (
                      <button
                        key={`ctx-ex-${i}`}
                        type="button"
                        onClick={() => setCustomerContext(ex)}
                        className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[11px] text-gray-700 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-800 transition-colors"
                        data-testid={`reply-customer-context-example-${i}`}
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* AI v2: 3 reply options as selectable cards */}
              {replyOptions.length > 0 ? (
                <div className="mt-5 space-y-2" data-testid="reply-options-list">
                  <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                    {t("lead.replyOptionsLabel")}
                  </p>
                  {/* Reply option cards. Stacked vertically on tablet
                      portrait and mobile so each card has full width and
                      readable preview text. Desktop (lg+) keeps the
                      3-column row for fast side-by-side comparison. */}
                  <div className="grid gap-2 lg:grid-cols-3">
                    {replyOptions.map((opt, idx) => {
                      const active = idx === selectedOptionIdx;
                      return (
                        <button
                          key={`${opt.label}-${idx}`}
                          type="button"
                          onClick={() => pickReplyOption(idx)}
                          className={`text-left rounded-lg border p-2.5 md:p-3 transition-all ${
                            active
                              ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200"
                              : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30"
                          }`}
                          data-testid={`reply-option-card-${idx}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-0.5 md:mb-1">
                            <span className="text-[12px] font-bold text-gray-900 truncate">
                              {opt.label}
                            </span>
                            {active ? (
                              <Check className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                            ) : null}
                          </div>
                          {opt.explanation ? (
                            <p className="hidden md:block text-[11px] text-gray-500 italic mb-1.5 line-clamp-2">
                              {opt.explanation}
                            </p>
                          ) : null}
                          <p className="text-[12px] text-gray-700 line-clamp-2 lg:line-clamp-4 leading-snug">
                            {opt.body}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* Generated reply editor. On mobile we use a smaller
                  initial height (rows={4}) and opt into the modern
                  `field-sizing: content` so the textarea auto-grows as
                  the owner edits — taking only the room it needs.
                  Desktop keeps a roomier baseline via min-h. */}
              <Textarea
                rows={4}
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                placeholder={generatingReply ? t("lead.replyPlaceholderBusy") : t("lead.replyPlaceholderIdle")}
                style={{ fieldSizing: "content" }}
                className="mt-5 bg-white/80 border-green-100/60 focus-visible:ring-green-500/30 font-normal text-sm leading-relaxed min-h-[6rem] md:min-h-[14rem]"
                data-testid="ai-reply-textarea"
              />
              <div className="mt-3 flex justify-end">
                <Button
                  onClick={saveReply}
                  disabled={savingReply || !replyDirty}
                  variant="outline"
                  size="sm"
                  className="bg-white"
                  data-testid="save-reply-button"
                >
                  {savingReply ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" /> {replyDirty ? t("lead.saveEdits") : t("lead.saved")}</>}
                </Button>
              </div>
            </div>

            {/* Mobile-only quick-action bar — sits BETWEEN Step 1 and the
                Step 2 / Step 3 cards so cleaning owners draft the reply
                first, then mark progress / copy / add to calendar. The
                full bar (including conditional Review/Rebook buttons) is
                rendered above the grid for tablet+ via the original
                `data-testid="quick-actions"` block.

                We keep only the 4 primary actions here (Mark Contacted,
                Mark Quoted, Copy Reply + Quote, Add to Calendar) — the
                Review/Rebook buttons are status-gated and remain in the
                expanded mobile flow further down. */}
            {!locked && (
              <div
                className="md:hidden flex flex-wrap items-center gap-2 order-3 lg:order-none"
                data-testid="quick-actions-mobile"
              >
                <Button
                  variant="outline"
                  onClick={() => quickStatus("contacted", setMarkingContacted)}
                  disabled={markingContacted || lead.status === "contacted"}
                  className="h-9 bg-white flex-1 min-w-[44%]"
                  data-testid="mark-contacted-button-mobile"
                >
                  {markingContacted ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <MailCheck className="w-3.5 h-3.5 mr-1.5" />}
                  {t("lead.markContacted")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => quickStatus("quoted", setMarkingQuoted)}
                  disabled={markingQuoted || lead.status === "quoted"}
                  className="h-9 bg-white flex-1 min-w-[44%]"
                  data-testid="mark-quoted-button-mobile"
                >
                  {markingQuoted ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <BadgeDollarSign className="w-3.5 h-3.5 mr-1.5" />}
                  {t("lead.markQuoted")}
                </Button>
                <Button
                  variant="outline"
                  onClick={copyBoth}
                  disabled={!replyDraft?.trim() && !quoteDraft?.trim()}
                  className="h-9 bg-white flex-1 min-w-[44%]"
                  data-testid="copy-reply-quote-button-mobile"
                >
                  {copied.both ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <FilePlus className="w-3.5 h-3.5 mr-1.5" />}
                  {t("lead.copyReplyQuote")}
                </Button>
                <Button
                  variant="outline"
                  onClick={openCalendar}
                  className="h-9 bg-white flex-1 min-w-[44%]"
                  data-testid="add-to-calendar-button-mobile"
                >
                  <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
                  {t("lead.addToCalendar")}
                </Button>
              </div>
            )}

            {/* Workflow guidance — reply ready, price not yet generated.
                Lives directly below the AI Reply card so the next-best
                action is visible exactly where the user is working. */}
            {step1Done && !step2Done && flowActive ? (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 md:p-4 flex items-start gap-3 order-4 lg:order-none"
                data-testid="guidance-reply-ready"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-emerald-900">
                    {t("lead.guide.replyReady")}
                  </p>
                  <p className="mt-0.5 text-xs text-emerald-800/80 leading-relaxed">
                    {t("lead.guide.replyReadyBody")}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => scrollToRef(aiQuoteCardRef)}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white h-9 shrink-0"
                  data-testid="guidance-reply-ready-cta"
                >
                  {t("lead.guide.replyReadyCta")}
                </Button>
              </div>
            ) : null}

            <div ref={aiQuoteCardRef} className={`rounded-2xl bg-white border border-gray-200 p-4 md:p-6 order-5 lg:order-none ${step2Active ? stepRing : ""}`} data-testid="ai-quote-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <StepChip n={2} state={chipState(step2Active, step2Done)} />
                  <div className="w-10 h-10 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-sky-700" />
                  </div>
                  <div>
                    <p className="font-display font-medium text-gray-900">{t("lead.aiQuoteTitle")}</p>
                    <p className="text-xs text-gray-500">{t("lead.aiQuoteSubtitle")}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <Select value={quoteLang} onValueChange={setQuoteLang}>
                    <SelectTrigger className="h-9 w-[120px] bg-white" data-testid="quote-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">{LANGUAGE_LABELS.en}</SelectItem>
                      <SelectItem value="es">{LANGUAGE_LABELS.es}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyText(quoteDraft, "quote", "Quote")}
                    disabled={!quoteDraft?.trim()}
                    data-testid="copy-quote-button"
                  >
                    {copied.quote ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                    {t("lead.copyQuote")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={genQuote}
                    disabled={generatingQuote}
                    className="bg-sky-700 hover:bg-sky-800 text-white"
                    data-testid="generate-quote-button"
                  >
                    {generatingQuote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (lead.ai_quote ? t("common.regenerate") : t("lead.generateQuote"))}
                  </Button>
                </div>
              </div>
              <div className="mt-3" data-testid="quote-helper-text">
                <p className="text-sm font-medium text-gray-800">{t("lead.aiQuoteHelper")}</p>
                <p className="mt-0.5 text-xs text-gray-500">{t("lead.aiQuoteHelperSub")}</p>
              </div>

              {/* AI v2: structured quote cards (only when ai_quote_structured
                  is present). Old leads with plain-text ai_quote render via
                  the legacy textarea below as before. */}
              {lead.ai_quote_structured ? (
                <StructuredQuoteView
                  quote={lead.ai_quote_structured}
                  t={t}
                  onPriceSave={savePriceEdit}
                  onMessageSave={saveCustomerMessageEdit}
                  priceSaving={quotePriceSaving}
                  messageSaving={quoteMessageSaving}
                />
              ) : null}

              {/* Legacy textarea — only shown when there's NO structured
                  quote. The structured cards above already cover every
                  field (price, range, why, included, upsells, customer
                  message, assumptions, next-best-action) so the raw
                  "Suggested Price: $XXX..." dump used to be a duplicate
                  / debug-style summary. Owners reported it created
                  confusion (looked like internal data might leak to the
                  customer). Hide it when the structured payload exists. */}
              {!lead.ai_quote_structured && (
                <>
                  <Textarea
                    rows={8}
                    value={quoteDraft}
                    onChange={(e) => setQuoteDraft(e.target.value)}
                    placeholder={generatingQuote ? t("lead.quotePlaceholderBusy") : t("lead.quotePlaceholderIdle")}
                    className="mt-5 bg-gray-50 border-gray-100 focus-visible:ring-sky-500/30 font-normal text-sm leading-relaxed"
                    data-testid="ai-quote-textarea"
                  />
                  <div className="mt-3 flex justify-end">
                    <Button
                      onClick={saveQuote}
                      disabled={savingQuote || !quoteDirty}
                      variant="outline"
                      size="sm"
                      data-testid="save-quote-button"
                    >
                      {savingQuote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" /> {quoteDirty ? t("lead.saveEdits") : t("lead.saved")}</>}
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Workflow guidance — price ready, quote link not yet created.
                Visible directly under the AI Price Estimate so the next CTA
                is unmissable. */}
            {step2Done && !step3Done && flowActive ? (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 md:p-4 flex items-start gap-3 order-6 lg:order-none"
                data-testid="guidance-price-ready"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-emerald-900">
                    {t("lead.guide.priceReady")}
                  </p>
                  <p className="mt-0.5 text-xs text-emerald-800/80 leading-relaxed">
                    {t("lead.guide.priceReadyBody")}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    scrollToRef(quoteLinkCardRef);
                    // Try to focus the create button right after scrolling
                    // so the next click is a single tap on mobile.
                    setTimeout(() => createQuoteBtnRef.current?.focus(), 400);
                  }}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white h-9 shrink-0"
                  data-testid="guidance-price-ready-cta"
                >
                  {t("lead.guide.priceReadyCta")}
                </Button>
              </div>
            ) : null}

            {/* Quote link card */}
            <div ref={quoteLinkCardRef} className={`rounded-2xl bg-white border border-gray-200 p-4 md:p-6 order-6 lg:order-none ${step3Active ? stepRing : ""}`} data-testid="quote-link-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <StepChip n={3} state={chipState(step3Active, step3Done)} />
                  <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="font-display font-medium text-gray-900">{t("lead.quoteLinkTitle")}</p>
                    <p className="text-xs text-gray-500">
                      {t("lead.quoteLinkSubtitle")}
                    </p>
                  </div>
                </div>
                {!quoteLink && (
                  <Button
                    ref={createQuoteBtnRef}
                    size="sm"
                    onClick={createQuoteLink}
                    disabled={creatingQuoteLink}
                    className="bg-green-800 hover:bg-green-900 text-white"
                    data-testid="create-quote-link-button"
                  >
                    {creatingQuoteLink ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Link2 className="w-3.5 h-3.5 mr-1.5" /> {t("lead.createQuoteLink")}
                      </>
                    )}
                  </Button>
                )}
              </div>

              {quoteLink && (
                <div
                  className="mt-5 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 flex items-start gap-3"
                  data-testid="guidance-quote-ready"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-emerald-900">
                      {t("lead.guide.quoteReady")}
                    </p>
                    <p className="mt-0.5 text-xs text-emerald-800/80 leading-relaxed">
                      {t("lead.guide.quoteReadyBody")}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={copyQuoteMessage}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white h-9 shrink-0"
                    data-testid="copy-quote-message-button"
                  >
                    {copied.message ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                    {t("lead.guide.quoteReadyCta")}
                  </Button>
                </div>
              )}

              {quoteLink && (
                <div
                  className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 overflow-hidden"
                  data-testid="quote-link-display"
                >
                  {/* URL row — full-width, single-line truncated. We use
                      `truncate` (overflow:hidden + text-overflow:ellipsis +
                      whitespace:nowrap) inside a flex item that owns
                      `min-w-0` so it actually contracts in narrow layouts.
                      Avoids the ugly per-character vertical break-all. */}
                  <div className="flex items-center gap-2 min-w-0 w-full max-w-full rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <Link2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <code
                      className="flex-1 min-w-0 text-xs text-gray-700 font-mono truncate"
                      title={quoteLink}
                      data-testid="quote-link-value"
                    >
                      {quoteLink}
                    </code>
                  </div>

                  {/* Action row — wraps to new lines on narrow screens; on
                      mobile (<640px) buttons stretch full-width via the
                      child flex-1 + sm:flex-initial pattern. Primary
                      "Copy quote message" lives in the success banner
                      above — keeping it duplicated here was creating
                      decision fatigue and washing out the visual
                      hierarchy. This row is now secondary actions only. */}
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`${quoteLink}${quoteLink.includes("?") ? "&" : "?"}preview=1`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 flex-1 sm:flex-initial"
                      data-testid="quote-link-preview"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> {t("lead.previewLink")}
                    </a>
                    <a
                      href={buildQuoteMailto()}
                      className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 flex-1 sm:flex-initial"
                      data-testid="send-via-email-button"
                    >
                      <Mail className="w-3.5 h-3.5" /> {t("lead.sendViaEmail")}
                    </a>
                    <Button
                      size="sm"
                      onClick={sendViaWhatsApp}
                      disabled={!waPhone}
                      className="h-9 bg-[#25D366] hover:bg-[#1ebe5b] text-white disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-initial"
                      title={
                        !waPhone
                          ? t("lead.waNoPhone")
                          : (quoteLang === "es" || replyLang === "es")
                            ? t("lead.waQuoteEsTitle")
                            : t("lead.waQuoteEnTitle")
                      }
                      data-testid="send-via-whatsapp-button"
                    >
                      <MessageCircle className="w-3.5 h-3.5 mr-1" />
                      {t("lead.sendViaWhatsApp")}
                    </Button>
                  </div>
                </div>
              )}

              {quoteLink && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={createQuoteLink}
                  disabled={creatingQuoteLink}
                  className="mt-3"
                  data-testid="regenerate-quote-link-button"
                >
                  {creatingQuoteLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("lead.newLink")}
                </Button>
              )}

              <div
                className="mt-5 rounded-xl border border-green-200 bg-green-50/80 p-4 sm:p-5"
                data-testid="quote-link-helper"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-full bg-green-600 text-white flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className="font-display text-base font-semibold text-green-900"
                      data-testid="quote-ready-title"
                    >
                      {t("lead.step3InstructionsTitle")}
                    </p>
                    <ol
                      className="mt-2 space-y-1.5 text-sm text-green-900/90 leading-relaxed list-decimal pl-5 marker:font-semibold marker:text-green-700"
                      data-testid="quote-ready-steps"
                    >
                      <li>{t("lead.step3InstructionsStep1")}</li>
                      <li>{t("lead.step3InstructionsStep2")}</li>
                      <li>{t("lead.step3InstructionsStep3")}</li>
                      <li>{t("lead.step3InstructionsStep4")}</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
          </>
        )}
      </main>
      <RebookPreviewDialog
        open={rebookPreviewOpen}
        onOpenChange={setRebookPreviewOpen}
        customerName={lead?.name}
        user={user}
        ctaUrl={(() => {
          // Mirror backend `_build_returning_customer_form_url` so the
          // preview matches what the customer will actually receive.
          if (!lead || !user?.id) return null;
          const params = new URLSearchParams();
          params.set("returning_customer", "true");
          params.set("previous_lead_id", lead.id);
          if (lead.name) params.set("customer_name", lead.name);
          if (lead.email) params.set("customer_email", lead.email);
          if (lead.phone) params.set("customer_phone", lead.phone);
          return `${window.location.origin}/l/${user.id}?${params.toString()}`;
        })()}
        triggerTestId="rebook-preview-dialog"
      />
      <ReviewRequestPreviewDialog
        open={reviewPreviewOpen}
        onOpenChange={setReviewPreviewOpen}
        customerName={lead?.name}
        user={user}
        reviewLink={(user?.google_review_link || "").trim() || null}
      />
    </div>
  );
}
